"use client";

import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";
import { Search, Play, Loader2, ArrowLeft, Upload, BarChart3, FileText, PieChart, Save, MessageSquare } from "lucide-react";
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { DocumentManager } from "../../planning/DocumentManager";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { ReviewComment } from "../../shared/ReviewComment";
import { getCompanyName } from "@/lib/textron-data-processor";
import { 
  getProcedureFileUsageForWorkspace, 
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry
} from "@/utils/fileUsageReference";
import { formatNumber } from "@/utils/formatting";

interface PPEAdditionsDeletionsProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
  workspaceName?: string;
  procedureName?: string;
}

interface ExcelColumn {
  name: string;
  value: string;
}

export default function PPEAdditionsDeletions({ onBack, setActiveSection, workspaceName, procedureName }: PPEAdditionsDeletionsProps) {
  const [additionsFile, setAdditionsFile] = useState<string>("");
  const [deletionsFile, setDeletionsFile] = useState<string>("");
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [fileUrl, setFileUrl] = useState<string>("");
  const [tableData, setTableData] = useState<any>(null);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [showAnalysisSelection, setShowAnalysisSelection] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());
  const [columnAnalyses, setColumnAnalyses] = useState<Record<string, any>>({});
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);
  const [additionsTableData, setAdditionsTableData] = useState<any[]>([]);
  const [deletionsTableData, setDeletionsTableData] = useState<any[]>([]);

  // Table response states for saving
  const [additionsTextResponse, setAdditionsTextResponse] = useState<any>(null);
  const [additionsDocuments, setAdditionsDocuments] = useState<any[]>([]);
  const [deletionsTextResponse, setDeletionsTextResponse] = useState<any>(null);
  const [deletionsDocuments, setDeletionsDocuments] = useState<any[]>([]);
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);
  
  // File search states
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `PPE_ADDITIONS_DELETIONS_${normalizedSection}`;
  };

  // Column loading states
  const [isLoadingAddCols, setIsLoadingAddCols] = useState(false);
  const [isLoadingDelCols, setIsLoadingDelCols] = useState(false);
  const [additionColumns, setAdditionColumns] = useState<ExcelColumn[]>([]);
  const [deletionColumns, setDeletionColumns] = useState<ExcelColumn[]>([]);

  // Single select column states
  const [additionsDateCol, setAdditionsDateCol] = useState<string>("");
  const [additionsAmountCol, setAdditionsAmountCol] = useState<string>("");
  const [deletionsDateCol, setDeletionsDateCol] = useState<string>("");
  const [deletionsAmountCol, setDeletionsAmountCol] = useState<string>("");

  // Composition Analysis states
  const [additionsCompositionColumns, setAdditionsCompositionColumns] = useState<Set<string>>(new Set());
  const [deletionsCompositionColumns, setDeletionsCompositionColumns] = useState<Set<string>>(new Set());
  const [additionsCompositionData, setAdditionsCompositionData] = useState<any[]>([]);
  const [deletionsCompositionData, setDeletionsCompositionData] = useState<any[]>([]);
  const [additionsCompositionResults, setAdditionsCompositionResults] = useState<Record<string, any>>({});
  const [deletionsCompositionResults, setDeletionsCompositionResults] = useState<Record<string, any>>({});
  const [isRunningCompositionAnalysis, setIsRunningCompositionAnalysis] = useState(false);
  const [showAdditionsCompositionDropdown, setShowAdditionsCompositionDropdown] = useState(false);
  const [showDeletionsCompositionDropdown, setShowDeletionsCompositionDropdown] = useState(false);
  const [additionsCompositionColumnsSearch, setAdditionsCompositionColumnsSearch] = useState("");
  const [deletionsCompositionColumnsSearch, setDeletionsCompositionColumnsSearch] = useState("");
  const additionsDropdownRef = useRef<HTMLDivElement>(null);
  const deletionsDropdownRef = useRef<HTMLDivElement>(null);
  const [additionsCompositionAmountCol, setAdditionsCompositionAmountCol] = useState<string>("");
  const [deletionsCompositionAmountCol, setDeletionsCompositionAmountCol] = useState<string>("");
  const [isRunningAdditionsCompositionAnalysis, setIsRunningAdditionsCompositionAnalysis] = useState(false);
  const [isRunningDeletionsCompositionAnalysis, setIsRunningDeletionsCompositionAnalysis] = useState(false);
  const [loadedAdditionsCompositionData, setLoadedAdditionsCompositionData] = useState<Record<string, any>>({});
  const [loadedDeletionsCompositionData, setLoadedDeletionsCompositionData] = useState<Record<string, any>>({});
  const [isLoadingCompositionAnalysis, setIsLoadingCompositionAnalysis] = useState(false);
  const [isLoadingDeletionsAnalysis, setIsLoadingDeletionsAnalysis] = useState(false);

  // State for Composition Analysis textResponse and documents (keyed by column name)
  const [additionsCompositionResponses, setAdditionsCompositionResponses] = useState<Record<string, { textResponse?: any; documents?: any[] }>>({});
  const [deletionsCompositionResponses, setDeletionsCompositionResponses] = useState<Record<string, { textResponse?: any; documents?: any[] }>>({});

  // Save status for each column
  const [additionsSaveStatus, setAdditionsSaveStatus] = useState<Record<string, 'idle' | 'saving' | 'saved' | 'error'>>({});
  const [deletionsSaveStatus, setDeletionsSaveStatus] = useState<Record<string, 'idle' | 'saving' | 'saved' | 'error'>>({});

  // Save all composition analysis data
  const [isSavingAllComposition, setIsSavingAllComposition] = useState(false);
  const [saveAllStatus, setSaveAllStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Save Additions Composition Analysis state
  const [isSavingAdditionsComposition, setIsSavingAdditionsComposition] = useState(false);
  const [saveAdditionsCompositionStatus, setSaveAdditionsCompositionStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveAdditionsCompositionError, setSaveAdditionsCompositionError] = useState<string | null>(null);

  // Save Deletions Composition Analysis state
  const [isSavingDeletionsComposition, setIsSavingDeletionsComposition] = useState(false);
  const [saveDeletionsCompositionStatus, setSaveDeletionsCompositionStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveDeletionsCompositionError, setSaveDeletionsCompositionError] = useState<string | null>(null);

  // Debug state for troubleshooting
  const [debugInfo, setDebugInfo] = useState<{
    additions?: {
      step?: string;
      stdout?: string;
      parsed?: any;
      chartData?: any;
      error?: string;
      details?: string;
    };
    deletions?: {
      step?: string;
      stdout?: string;
      parsed?: any;
      chartData?: any;
      error?: string;
      details?: string;
    };
    compAnal?: {
      step?: string;
      stdout?: string;
      parsed?: any;
      data?: any;
      error?: string;
      details?: string;
    };
  }>({});

  // Auto-save debounce refs
  const additionsSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const deletionsSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Flag to prevent auto-save during initial load
  const isInitialLoadRef = useRef(true);

  // Flag to prevent auto-save during manual save operation
  const isManualSaveInProgressRef = useRef(false);


  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (additionsDropdownRef.current && !additionsDropdownRef.current.contains(event.target as Node)) {
        setShowAdditionsCompositionDropdown(false);
      }
      if (deletionsDropdownRef.current && !deletionsDropdownRef.current.contains(event.target as Node)) {
        setShowDeletionsCompositionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_PPE");
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Property, Plant & Equipment", "Additions & Deletions Analysis");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  // Call searchForFiles when component mounts
  useEffect(() => {
    searchForFiles();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Call downloadAndDisplayTable when component mounts
  useEffect(() => {
    downloadAndDisplayTable();
  }, []);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_PPE_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const fileName = `Execution_PPE_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "PPE-ADL-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: getFY_YEAR(),
                sectionList: ["Execution_PPE"],
              });

              if (result.success) {
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadAdditionColumns = async () => {
    if (!additionsFile) return;
    setIsLoadingAddCols(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(additionsFile);
        if (result.ok && result.columns) {
          setAdditionColumns(result.columns.map((c: string) => ({ name: c, value: c })));
          // Save file name to file usage reference
          const fileName = additionsFile.includes(' (') ? additionsFile.split(' (')[0] : additionsFile;
          await updateProcedureFileUsageForWorkspace("Property, Plant & Equipment", "Additions & Deletions Analysis", [{
            "Search for Files - Additions": fileName
          }]);
          await loadFileUsageReference();
        }
      }
      // Load Excel data for composition analysis
      await loadAdditionsExcelData();
    } catch (e) {
      console.error("Error loading addition columns:", e);
    } finally {
      setIsLoadingAddCols(false);
    }
  };

  const loadDeletionColumns = async () => {
    if (!deletionsFile) return;
    setIsLoadingDelCols(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(deletionsFile);
        if (result.ok && result.columns) {
          setDeletionColumns(result.columns.map((c: string) => ({ name: c, value: c })));
          // Save file name to file usage reference
          const fileName = deletionsFile.includes(' (') ? deletionsFile.split(' (')[0] : deletionsFile;
          await updateProcedureFileUsageForWorkspace("Property, Plant & Equipment", "Additions & Deletions Analysis", [{
            "Search for Files - Deletions": fileName
          }]);
          await loadFileUsageReference();
        }
      }
      // Load Excel data for composition analysis
      await loadDeletionsExcelData();
    } catch (e) {
      console.error("Error loading deletion columns:", e);
    } finally {
      setIsLoadingDelCols(false);
    }
  };

  // Load Excel data for composition analysis
  const loadAdditionsExcelData = async () => {
    if (!additionsFile) return;
    try {
      // For now, we'll use the existing table data if available
      // In a real implementation, you might want to load the full Excel file
      // This is a placeholder - you may need to add an API call to load full Excel data
      console.log("Loading additions Excel data for composition analysis");
    } catch (e) {
      console.error("Error loading additions Excel data:", e);
    }
  };

  const loadDeletionsExcelData = async () => {
    if (!deletionsFile) return;
    try {
      // For now, we'll use the existing table data if available
      // In a real implementation, you might want to load the full Excel file
      // This is a placeholder - you may need to add an API call to load full Excel data
      console.log("Loading deletions Excel data for composition analysis");
    } catch (e) {
      console.error("Error loading deletions Excel data:", e);
    }
  };

  // Handle composition column selection
  const handleAdditionsCompositionColumnToggle = (column: string) => {
    setAdditionsCompositionColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(column)) {
        newSet.delete(column);
      } else {
        newSet.add(column);
      }
      return newSet;
    });
  };

  const handleDeletionsCompositionColumnToggle = (column: string) => {
    setDeletionsCompositionColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(column)) {
        newSet.delete(column);
      } else {
        newSet.add(column);
      }
      return newSet;
    });
  };

  // Run Additions Composition Analysis
  const runAdditionsCompositionAnalysis = async () => {
    if (additionsCompositionColumns.size === 0 || !additionsCompositionAmountCol) return;

    setIsRunningAdditionsCompositionAnalysis(true);
    try {
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_ppe_additions_composition_analysis", {
          inputFiles: [],
          options: {
            group_columns: Array.from(additionsCompositionColumns),
            amount_column: additionsCompositionAmountCol,
          },
        });

        if (result.ok && result.runId) {
          const unsubscribe = window.payroll.onProgress((payload: any) => {
            if (payload.runId !== result.runId) return;

            if (payload.status === "success") {
              try {
                const lines = String(payload.stdout || '').split('\n');
                const jsonLine = lines.find((l: string) => l.trim().startsWith('{') && l.includes('success'));
                if (jsonLine) {
                  const parsed = JSON.parse(jsonLine);
                  if (parsed.success) {
                    console.log("Additions Composition Analysis completed:", parsed);
                  }
                }
              } catch (e) {
                console.error("Error parsing result:", e);
              }
              setIsRunningAdditionsCompositionAnalysis(false);
              unsubscribe();
            } else if (payload.status === "error") {
              console.error("Additions composition analysis error:", payload.error);
              setIsRunningAdditionsCompositionAnalysis(false);
              unsubscribe();
            }
          });
        } else {
          setIsRunningAdditionsCompositionAnalysis(false);
        }
      } else {
        setIsRunningAdditionsCompositionAnalysis(false);
      }
    } catch (error) {
      console.error("Error running additions composition analysis:", error);
      setIsRunningAdditionsCompositionAnalysis(false);
    }
  };

  // Helper function to extract JSON from stdout
  const extractJSONFromStdout = (stdout: string): any | null => {
    try {
      const normalized = stdout.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      const lines = normalized.split('\n');

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith('{') && trimmed.includes('"success"') && trimmed.includes('"data"')) {
          try {
            const cleanLine = trimmed.replace(/\r$/, '').trim();
            const parsed = JSON.parse(cleanLine);
            if (parsed.success && parsed.data) {
              return parsed.data;
            }
          } catch (parseError) {
            // Try regex extraction
            try {
              const jsonMatch = trimmed.match(/\{.*"success".*"data".*\}/);
              if (jsonMatch) {
                const parsed = JSON.parse(jsonMatch[0]);
                if (parsed.success && parsed.data) {
                  return parsed.data;
                }
              }
            } catch { }
          }
        }
      }
    } catch (e) {
      console.error("Error extracting JSON:", e);
    }
    return null;
  };

  // Load Composition Analysis (Additions)
  const loadCompositionAnalysis = async () => {
    console.log(" loadCompositionAnalysis called");
    setIsLoadingCompositionAnalysis(true);
    setDebugInfo(prev => ({ ...prev, additions: { step: "Starting", details: "Function called" } }));

    try {
      if (!window.payroll?.run) {
        setDebugInfo(prev => ({ ...prev, additions: { step: "Error", error: "window.payroll.run not available" } }));
        setIsLoadingCompositionAnalysis(false);
        return;
      }

      // Step 1: Load chart data from Execution_PPE_ALCompAnls.json
      setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Calling download_ppe_alcomp_anls script", details: "Waiting for script to start..." } }));
      let chartData: Record<string, any> | null = null;
      let debugStdout = "";
      let debugParsed: any = null;
      let debugError: string | undefined = undefined;

      try {
        // Set up listener FIRST, before calling run(), to ensure we catch all payloads
        let allStdout = "";
        let statusReceived = false;
        let finalPayload: any = null;
        let payloadCount = 0;
        let expectedRunId: string | null = null;

        const pendingPayloads: any[] = []; // Store payloads that arrive before we know the runId

        const unsubscribe = window.payroll.onProgress((payload: any) => {
            payloadCount++;
            const payloadRunId = String(payload.runId || '');

            // If we don't have expectedRunId yet, store the payload
            if (!expectedRunId) {
              pendingPayloads.push(payload);
              return;
            }

            const matches = payloadRunId === expectedRunId;

            console.log(` Received payload #${payloadCount} for runId: "${payloadRunId}", expected: "${expectedRunId}", match: ${matches}`, "Payload:", payload);

            if (!matches) {
              console.log(" Payload runId mismatch, ignoring");
              return;
            }

            // Capture all payloads for debugging
            finalPayload = { ...payload, receivedAt: new Date().toISOString() };
            setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Payload received", details: `Status: ${payload.status}, Has stdout: ${!!payload.stdout}, Stdout length: ${payload.stdout ? String(payload.stdout).length : 0}` } }));
            console.log(" Payload matched! Status:", payload.status, "Has stdout:", !!payload.stdout, "Stdout length:", payload.stdout ? String(payload.stdout).length : 0);

            // Accumulate stdout from any payload that has it
            if (payload.stdout) {
              allStdout = String(payload.stdout); // Use the full accumulated stdout from payload
              debugStdout = allStdout;
              setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Processing stdout", details: `Stdout length: ${allStdout.length}` } }));
              console.log(" Got stdout, length:", allStdout.length);
              try {
                const extracted = extractJSONFromStdout(allStdout);
                debugParsed = extracted;
                if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                  chartData = extracted;
                  setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Data extracted", details: `Chart data keys: ${Object.keys(extracted).join(', ')}` } }));
                  console.log(" Extracted additions chart data with keys:", Object.keys(extracted));
                } else {
                  setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Extraction failed", details: "Extracted data is empty or invalid" } }));
                }
              } catch (e) {
                debugError = `Error extracting JSON: ${e}`;
                setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: JSON parse error", details: String(e) } }));
                console.error(" Error extracting JSON:", e);
              }
            }

            // Check for success status
            if (payload.status === "success") {
              setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Success status received", details: "Script completed successfully" } }));
              console.log(" Success status received!");
              statusReceived = true;
              // Use the stdout from this payload (it should have the full accumulated stdout)
              if (payload.stdout) {
                allStdout = String(payload.stdout);
                debugStdout = allStdout;
              }
              // Try to extract data one more time with the final stdout
              if (!chartData && allStdout) {
                try {
                  const extracted = extractJSONFromStdout(allStdout);
                  debugParsed = extracted;
                  if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                    chartData = extracted;
                    setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Data extracted on success", details: `Chart data keys: ${Object.keys(extracted).join(', ')}` } }));
                    console.log(" Extracted additions chart data on success with keys:", Object.keys(extracted));
                  }
                } catch (e) {
                  debugError = `Error extracting JSON on success: ${e}`;
                  console.error(" Error extracting JSON on success:", e);
                }
              }
            }

            // Check for error status
            if (payload.status === "error" || payload.error) {
              debugError = `Script error: ${payload.error || payload.message || 'Unknown error'}`;
              statusReceived = true; // Mark as received so we exit
              console.error(" Error status received:", debugError);
            }
          });

        const chartResult = await window.payroll.run("download_ppe_alcomp_anls", {
          inputFiles: [],
          options: {},
        });

        setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Script called", details: `Result: ok=${chartResult.ok}, runId=${chartResult.runId}, error=${chartResult.error || 'none'}` } }));

        if (chartResult.ok && chartResult.runId) {
          expectedRunId = String(chartResult.runId);

          setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Listening for payloads", details: `Expected runId: ${expectedRunId}` } }));
          console.log(" Starting to listen for additions runId:", expectedRunId, "Type:", typeof chartResult.runId);

          // Process any pending payloads that arrived before we knew the runId
          for (const pendingPayload of pendingPayloads) {
            const pendingRunId = String(pendingPayload.runId || '');
            if (pendingRunId === expectedRunId) {
              const payload = pendingPayload;
              payloadCount++;

              // Capture all payloads for debugging
              finalPayload = { ...payload, receivedAt: new Date().toISOString() };
              setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Payload received", details: `Status: ${payload.status}, Has stdout: ${!!payload.stdout}, Stdout length: ${payload.stdout ? String(payload.stdout).length : 0}` } }));
              console.log(" Payload matched! Status:", payload.status, "Has stdout:", !!payload.stdout, "Stdout length:", payload.stdout ? String(payload.stdout).length : 0);

              // Accumulate stdout from any payload that has it
              if (payload.stdout) {
                allStdout = String(payload.stdout);
                debugStdout = allStdout;
                console.log(" Got stdout, length:", allStdout.length);
                try {
                  const extracted = extractJSONFromStdout(allStdout);
                  debugParsed = extracted;
                  if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                    chartData = extracted;
                    setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Data extracted", details: `Chart data keys: ${Object.keys(extracted).join(', ')}` } }));
                    console.log(" Extracted additions chart data with keys:", Object.keys(extracted));
                  } else {
                    setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Extraction failed", details: "Extracted data is empty or invalid" } }));
                  }
                } catch (e) {
                  debugError = `Error extracting JSON: ${e}`;
                  setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: JSON parse error", details: String(e) } }));
                  console.error(" Error extracting JSON:", e);
                }
              }

              // Check for success status
              if (payload.status === "success") {
                setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Success status received", details: "Script completed successfully" } }));
                console.log(" Success status received!");
                statusReceived = true;
                if (payload.stdout) {
                  allStdout = String(payload.stdout);
                  debugStdout = allStdout;
                }
                if (!chartData && allStdout) {
                  try {
                    const extracted = extractJSONFromStdout(allStdout);
                    debugParsed = extracted;
                    if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                      chartData = extracted;
                      setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Data extracted on success", details: `Chart data keys: ${Object.keys(extracted).join(', ')}` } }));
                      console.log(" Extracted additions chart data on success with keys:", Object.keys(extracted));
                    }
                  } catch (e) {
                    debugError = `Error extracting JSON on success: ${e}`;
                    console.error(" Error extracting JSON on success:", e);
                  }
                }
              }

              // Check for error status
              if (payload.status === "error" || payload.error) {
                debugError = `Script error: ${payload.error || payload.message || 'Unknown error'}`;
                statusReceived = true;
                console.error(" Error status received:", debugError);
              }
            }
          }

          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              if (statusReceived) {
                console.log(" Status received, resolving promise. Payload count:", payloadCount);
                clearInterval(checkInterval);
                unsubscribe();
                resolve();
              }
            }, 100);
            setTimeout(() => {
              console.log("⏱️ Timeout reached. Status received:", statusReceived, "Payload count:", payloadCount, "Final payload:", finalPayload);
              clearInterval(checkInterval);
              unsubscribe();
              if (!statusReceived) {
                // If we have data but no status, that's okay - use the data
                if (chartData && Object.keys(chartData).length > 0) {
                  console.log(" Timeout but we have chart data, using it");
                  statusReceived = true; // Mark as received so we use the data
                  const keyCount = Object.keys(chartData).length;
                  setDebugInfo(prev => ({ ...prev, additions: { step: "Step 1: Timeout but data available", details: `Using chart data with ${keyCount} keys` } }));
                } else {
                  debugError = `Timeout waiting for script completion. Received ${payloadCount} payload(s). Last payload: ${finalPayload ? JSON.stringify(finalPayload).substring(0, 200) : 'none'}`;
                }
              }
              resolve();
            }, 30000); // Increased timeout to 30 seconds
          });

          console.log(" Finished waiting. Chart data:", chartData && Object.keys(chartData).length > 0 ? Object.keys(chartData) : null, "Status received:", statusReceived);
        } else {
          unsubscribe();
          debugError = `Script run failed: ${chartResult.error || 'Unknown error'}`;
        }
      } catch (error) {
        debugError = `Exception: ${error instanceof Error ? error.message : String(error)}`;
        console.error("Error loading chart data:", error);
      }

      // Step 2: Store chart data FIRST (before updating debug info)
      setDebugInfo(prev => ({ ...prev, additions: { step: "Step 2: Storing chart data", details: chartData ? `Data available with ${Object.keys(chartData).length} keys` : "No chart data available" } }));
      if (chartData && Object.keys(chartData).length > 0) {
        const clonedChartData = JSON.parse(JSON.stringify(chartData));
        console.log(" Setting loaded additions composition data:", Object.keys(clonedChartData), clonedChartData);
        setLoadedAdditionsCompositionData(clonedChartData);
        setDebugInfo(prev => ({ ...prev, additions: { step: "Step 2: Chart data stored", details: `Stored ${Object.keys(clonedChartData).length} chart columns` } }));
      } else {
        console.warn(" No chart data loaded for additions. chartData:", chartData, "debugError:", debugError);
        setDebugInfo(prev => ({ ...prev, additions: { step: "Step 2: No chart data", details: debugError || "No data extracted from script" } }));
      }

      // Update debug info - ALWAYS update, even if empty
      setDebugInfo(prev => ({
        ...prev,
        additions: {
          step: prev.additions?.step || "Unknown",
          details: prev.additions?.details || "",
          stdout: debugStdout || "",
          parsed: debugParsed || null,
          chartData: chartData || null,
          error: debugError || undefined
        }
      }));

      // Step 3: Load textResponse and documents from Execution_PPE_CompAnal.json
      setDebugInfo(prev => ({ ...prev, additions: { step: "Step 3: Loading CompAnal data", details: "Calling download_ppe_comp_anal script" } }));
      let compAnalDebugStdout = "";
      let compAnalDebugParsed: any = null;
      let compAnalDebugError: string | undefined = undefined;

      try {
        const compAnalResult = await window.payroll.run("download_ppe_comp_anal", {
          inputFiles: [],
          options: {},
        });

        setDebugInfo(prev => ({ ...prev, additions: { step: "Step 3: CompAnal script called", details: `Result: ok=${compAnalResult.ok}, runId=${compAnalResult.runId}` } }));

        if (compAnalResult.ok && compAnalResult.runId) {
          let allStdout = "";
          let statusReceived = false;
          let compAnalData: any = null;
          let finalPayload: any = null;
          const expectedRunId = String(compAnalResult.runId);

          console.log(" [CompAnal Additions] Starting to listen for runId:", expectedRunId);

          const unsubscribe = window.payroll.onProgress((payload: any) => {
            // Convert both to strings for comparison
            const payloadRunId = String(payload.runId || '');
            const matches = payloadRunId === expectedRunId;

            if (!matches) {
              return;
            }

            // Capture all payloads for debugging
            finalPayload = payload;

            // Accumulate stdout from any payload that has it
            if (payload.stdout) {
              allStdout = String(payload.stdout); // Use the full accumulated stdout from payload
              compAnalDebugStdout = allStdout;
              try {
                const extracted = extractJSONFromStdout(allStdout);
                compAnalDebugParsed = extracted;
                if (extracted && typeof extracted === 'object') {
                  compAnalData = extracted;
                  console.log(" [CompAnal Additions] Extracted data:", Object.keys(extracted));
                }
              } catch (e) {
                compAnalDebugError = `Error extracting JSON: ${e}`;
              }
            }

            // Check for success status
            if (payload.status === "success") {
              statusReceived = true;
              // Use the stdout from this payload (it should have the full accumulated stdout)
              if (payload.stdout) {
                allStdout = String(payload.stdout);
                compAnalDebugStdout = allStdout;
              }
              // Try to extract data one more time with the final stdout
              if (!compAnalData && allStdout) {
                try {
                  const extracted = extractJSONFromStdout(allStdout);
                  compAnalDebugParsed = extracted;
                  if (extracted && typeof extracted === 'object') {
                    compAnalData = extracted;
                    console.log(" [CompAnal Additions] Extracted data on success:", Object.keys(extracted));
                  }
                } catch (e) {
                  compAnalDebugError = `Error extracting JSON on success: ${e}`;
                }
              }
            }

            // Check for error status
            if (payload.status === "error" || payload.error) {
              compAnalDebugError = `Script error: ${payload.error || payload.message || 'Unknown error'}`;
              statusReceived = true; // Mark as received so we exit
            }
          });

          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              if (statusReceived) {
                clearInterval(checkInterval);
                unsubscribe();
                resolve();
              }
            }, 100);
            setTimeout(() => {
              clearInterval(checkInterval);
              unsubscribe();
              if (!statusReceived) {
                // If we have data but no status, that's okay - use the data
                if (compAnalData) {
                  console.log(" [CompAnal Additions] Timeout but we have data, using it");
                  statusReceived = true;
                } else {
                  compAnalDebugError = `Timeout waiting for script completion. Last payload: ${finalPayload ? JSON.stringify(finalPayload).substring(0, 200) : 'none'}`;
                }
              }
              resolve();
            }, 30000); // Increased timeout to 30 seconds
          });

          // Update debug info for CompAnal
          setDebugInfo(prev => ({
            ...prev,
            compAnal: {
              stdout: compAnalDebugStdout,
              parsed: compAnalDebugParsed,
              data: compAnalData,
              error: compAnalDebugError
            }
          }));

          // Process CompAnal data for Additions
          if (compAnalData && compAnalData.Additions && Array.isArray(compAnalData.Additions)) {
            const responses: Record<string, { textResponse?: any; documents?: any[] }> = {};
            for (const item of compAnalData.Additions) {
              if (item.name) {
                const columnName = item.name;
                if (item.text || (item.documents && item.documents.length > 0)) {
                  // Convert text to HTML format for DocumentManager
                  const textResponse = item.text ? { html: item.text, text: item.text } : undefined;

                  // Convert documents to DocumentManager format
                  const documents = item.documents ? item.documents.map((doc: any, idx: number) => ({
                    id: `loaded_${columnName}_${idx}`,
                    type: doc.url ? 'linked' as const : 'uploaded' as const,
                    name: doc.name || '',
                    url: doc.url || '',
                    reference: doc.reference || ''
                  })) : [];

                  responses[columnName] = {
                    textResponse,
                    documents
                  };

                  // Set localStorage for DocumentManager
                  if (item.text) {
                    localStorage.setItem(`textbox_${columnName}`, item.text);
                  }
                  if (documents.length > 0) {
                    localStorage.setItem(`documents_${columnName}`, JSON.stringify(documents));
                  }
                }
              }
            }
            if (Object.keys(responses).length > 0) {
              console.log(" Loaded additions composition responses from Execution_PPE_CompAnal.json:", Object.keys(responses));
              setAdditionsCompositionResponses((prev) => ({ ...prev, ...responses }));
            }
          }
        }
      } catch (err) {
        console.error("Failed to load additions composition responses from Execution_PPE_CompAnal.json", err);
      }
    } catch (error) {
      console.error("Error loading additions analysis:", error);
      // Update debug info with error
      setDebugInfo(prev => ({
        ...prev,
        additions: {
          ...prev.additions,
          error: error instanceof Error ? error.message : String(error)
        }
      }));
    } finally {
      setIsLoadingCompositionAnalysis(false);
    }
  };

  // Load Deletions Composition Analysis
  const loadDeletionsCompositionAnalysis = async () => {
    console.log(" loadDeletionsCompositionAnalysis called");
    setIsLoadingDeletionsAnalysis(true);
    setDebugInfo(prev => ({ ...prev, deletions: { step: "Starting", details: "Function called" } }));

    try {
      if (!window.payroll?.run) {
        setDebugInfo(prev => ({ ...prev, deletions: { step: "Error", error: "window.payroll.run not available" } }));
        setIsLoadingDeletionsAnalysis(false);
        return;
      }

      // Step 1: Load chart data from Execution_PPE_DLCompAnls.json
      setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 1: Calling download_ppe_dlcomp_anls script", details: "Waiting for script to start..." } }));
      let chartData: Record<string, any> | null = null;
      let debugStdout = "";
      let debugParsed: any = null;
      let debugError: string | undefined = undefined;

      try {
        // Set up listener FIRST, before calling run(), to ensure we catch all payloads
        let allStdout = "";
        let statusReceived = false;
        let finalPayload: any = null;
        let payloadCount = 0;
        let expectedRunId: string | null = null;

        const pendingPayloads: any[] = []; // Store payloads that arrive before we know the runId

        const unsubscribe = window.payroll.onProgress((payload: any) => {
            payloadCount++;
            const payloadRunId = String(payload.runId || '');

            // If we don't have expectedRunId yet, store the payload
            if (!expectedRunId) {
              pendingPayloads.push(payload);
              return;
            }

            const matches = payloadRunId === expectedRunId;

            console.log(` Received payload #${payloadCount} for runId: "${payloadRunId}", expected: "${expectedRunId}", match: ${matches}`, "Payload:", payload);

            if (!matches) {
              console.log(" Payload runId mismatch, ignoring");
              return;
            }

            setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 1: Payload received", details: `Status: ${payload.status}, Has stdout: ${!!payload.stdout}, Stdout length: ${payload.stdout ? String(payload.stdout).length : 0}` } }));

            // Capture all payloads for debugging
            finalPayload = { ...payload, receivedAt: new Date().toISOString() };
            console.log(" Payload matched! Status:", payload.status, "Has stdout:", !!payload.stdout, "Stdout length:", payload.stdout ? String(payload.stdout).length : 0);

            // Accumulate stdout from any payload that has it
            if (payload.stdout) {
              allStdout = String(payload.stdout); // Use the full accumulated stdout from payload
              debugStdout = allStdout;
              console.log(" Got stdout, length:", allStdout.length);
              try {
                const extracted = extractJSONFromStdout(allStdout);
                debugParsed = extracted;
                if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                  chartData = extracted;
                  console.log(" Extracted deletions chart data with keys:", Object.keys(extracted));
                }
              } catch (e) {
                debugError = `Error extracting JSON: ${e}`;
                console.error(" Error extracting JSON:", e);
              }
            }

            // Check for success status
            if (payload.status === "success") {
              console.log(" Success status received!");
              statusReceived = true;
              // Use the stdout from this payload (it should have the full accumulated stdout)
              if (payload.stdout) {
                allStdout = String(payload.stdout);
                debugStdout = allStdout;
              }
              // Try to extract data one more time with the final stdout
              if (!chartData && allStdout) {
                try {
                  const extracted = extractJSONFromStdout(allStdout);
                  debugParsed = extracted;
                  if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                    chartData = extracted;
                    console.log(" Extracted deletions chart data on success with keys:", Object.keys(extracted));
                  }
                } catch (e) {
                  debugError = `Error extracting JSON on success: ${e}`;
                  console.error(" Error extracting JSON on success:", e);
                }
              }
            }

            // Check for error status
            if (payload.status === "error" || payload.error) {
              debugError = `Script error: ${payload.error || payload.message || 'Unknown error'}`;
              statusReceived = true; // Mark as received so we exit
              console.error(" Error status received:", debugError);
            }
          });

        const chartResult = await window.payroll.run("download_ppe_dlcomp_anls", {
          inputFiles: [],
          options: {},
        });

        setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 1: Script called", details: `Result: ok=${chartResult.ok}, runId=${chartResult.runId}, error=${chartResult.error || 'none'}` } }));

        if (chartResult.ok && chartResult.runId) {
          expectedRunId = String(chartResult.runId);

          setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 1: Listening for payloads", details: `Expected runId: ${expectedRunId}` } }));
          console.log(" Starting to listen for deletions runId:", expectedRunId, "Type:", typeof chartResult.runId);

          // Process any pending payloads that arrived before we knew the runId
          for (const pendingPayload of pendingPayloads) {
            const pendingRunId = String(pendingPayload.runId || '');
            if (pendingRunId === expectedRunId) {
              const payload = pendingPayload;
              payloadCount++;

              setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 1: Payload received", details: `Status: ${payload.status}, Has stdout: ${!!payload.stdout}, Stdout length: ${payload.stdout ? String(payload.stdout).length : 0}` } }));

              // Capture all payloads for debugging
              finalPayload = { ...payload, receivedAt: new Date().toISOString() };
              console.log(" Payload matched! Status:", payload.status, "Has stdout:", !!payload.stdout, "Stdout length:", payload.stdout ? String(payload.stdout).length : 0);

              // Accumulate stdout from any payload that has it
              if (payload.stdout) {
                allStdout = String(payload.stdout);
                debugStdout = allStdout;
                console.log(" Got stdout, length:", allStdout.length);
                try {
                  const extracted = extractJSONFromStdout(allStdout);
                  debugParsed = extracted;
                  if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                    chartData = extracted;
                    console.log(" Extracted deletions chart data with keys:", Object.keys(extracted));
                  }
                } catch (e) {
                  debugError = `Error extracting JSON: ${e}`;
                  console.error(" Error extracting JSON:", e);
                }
              }

              // Check for success status
              if (payload.status === "success") {
                console.log(" Success status received!");
                statusReceived = true;
                if (payload.stdout) {
                  allStdout = String(payload.stdout);
                  debugStdout = allStdout;
                }
                if (!chartData && allStdout) {
                  try {
                    const extracted = extractJSONFromStdout(allStdout);
                    debugParsed = extracted;
                    if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                      chartData = extracted;
                      console.log(" Extracted deletions chart data on success with keys:", Object.keys(extracted));
                    }
                  } catch (e) {
                    debugError = `Error extracting JSON on success: ${e}`;
                    console.error(" Error extracting JSON on success:", e);
                  }
                }
              }

              // Check for error status
              if (payload.status === "error" || payload.error) {
                debugError = `Script error: ${payload.error || payload.message || 'Unknown error'}`;
                statusReceived = true;
                console.error(" Error status received:", debugError);
              }
            }
          }

          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              if (statusReceived) {
                console.log(" Status received, resolving promise. Payload count:", payloadCount);
                clearInterval(checkInterval);
                unsubscribe();
                resolve();
              }
            }, 100);
            setTimeout(() => {
              console.log("⏱️ Timeout reached. Status received:", statusReceived, "Payload count:", payloadCount, "Final payload:", finalPayload);
              clearInterval(checkInterval);
              unsubscribe();
              if (!statusReceived) {
                // If we have data but no status, that's okay - use the data
                if (chartData && Object.keys(chartData).length > 0) {
                  console.log(" Timeout but we have chart data, using it");
                  statusReceived = true;
                } else {
                  debugError = `Timeout waiting for script completion. Received ${payloadCount} payload(s). Last payload: ${finalPayload ? JSON.stringify(finalPayload) : 'none'}`;
                }
              }
              resolve();
            }, 30000); // Increased timeout to 30 seconds
          });

          console.log(" Finished waiting. Chart data:", chartData ? Object.keys(chartData) : null, "Status received:", statusReceived);
        } else {
          unsubscribe();
          debugError = `Script run failed: ${chartResult.error || 'Unknown error'}`;
        }
      } catch (error) {
        debugError = `Exception: ${error instanceof Error ? error.message : String(error)}`;
        console.error("Error loading deletions chart data:", error);
      }

      // Step 2: Store chart data FIRST (before updating debug info)
      setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 2: Storing chart data", details: chartData ? `Data available with ${Object.keys(chartData).length} keys` : "No chart data available" } }));
      if (chartData && Object.keys(chartData).length > 0) {
        const clonedChartData = JSON.parse(JSON.stringify(chartData));
        console.log(" Setting loaded deletions composition data:", Object.keys(clonedChartData), clonedChartData);
        setLoadedDeletionsCompositionData(clonedChartData);
        setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 2: Chart data stored", details: `Stored ${Object.keys(clonedChartData).length} chart columns` } }));
      } else {
        console.warn(" No chart data loaded for deletions. chartData:", chartData, "debugError:", debugError);
        setDebugInfo(prev => ({ ...prev, deletions: { step: "Step 2: No chart data", details: debugError || "No data extracted from script" } }));
      }

      // Update debug info - ALWAYS update, even if empty
      setDebugInfo(prev => ({
        ...prev,
        deletions: {
          step: prev.deletions?.step || "Unknown",
          details: prev.deletions?.details || "",
          stdout: debugStdout || "",
          parsed: debugParsed || null,
          chartData: chartData || null,
          error: debugError || undefined
        }
      }));

      // Step 3: Load textResponse and documents from Execution_PPE_CompAnal.json
      let compAnalDebugStdout2 = "";
      let compAnalDebugParsed2: any = null;
      let compAnalDebugError2: string | undefined = undefined;

      try {
        const compAnalResult = await window.payroll.run("download_ppe_comp_anal", {
          inputFiles: [],
          options: {},
        });

        if (compAnalResult.ok && compAnalResult.runId) {
          let allStdout = "";
          let statusReceived = false;
          let compAnalData: any = null;

          const unsubscribe = window.payroll.onProgress((payload: any) => {
            if (payload.runId !== compAnalResult.runId) return;
            if (payload.stdout) {
              allStdout += String(payload.stdout);
              compAnalDebugStdout2 = allStdout;
              try {
                const extracted = extractJSONFromStdout(allStdout);
                compAnalDebugParsed2 = extracted;
                if (extracted && typeof extracted === 'object') {
                  compAnalData = extracted;
                }
              } catch (e) {
                compAnalDebugError2 = `Error extracting JSON: ${e}`;
              }
            }
            if (payload.status === "success") {
              statusReceived = true;
              if (!compAnalData && allStdout) {
                try {
                  const extracted = extractJSONFromStdout(allStdout);
                  compAnalDebugParsed2 = extracted;
                  if (extracted && typeof extracted === 'object') {
                    compAnalData = extracted;
                  }
                } catch (e) {
                  compAnalDebugError2 = `Error extracting JSON on success: ${e}`;
                }
              }
            }
            if (payload.error) {
              compAnalDebugError2 = `Script error: ${payload.error}`;
            }
          });

          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              if (statusReceived) {
                clearInterval(checkInterval);
                unsubscribe();
                resolve();
              }
            }, 500);
            setTimeout(() => {
              clearInterval(checkInterval);
              unsubscribe();
              if (!statusReceived) {
                compAnalDebugError2 = "Timeout waiting for script completion";
              }
              resolve();
            }, 10000);
          });

          // Update debug info for CompAnal (deletions)
          setDebugInfo(prev => ({
            ...prev,
            compAnal: {
              stdout: compAnalDebugStdout2,
              parsed: compAnalDebugParsed2,
              data: compAnalData,
              error: compAnalDebugError2
            }
          }));

          // Process CompAnal data for Deletions
          if (compAnalData && compAnalData.Deletions && Array.isArray(compAnalData.Deletions)) {
            const responses: Record<string, { textResponse?: any; documents?: any[] }> = {};
            for (const item of compAnalData.Deletions) {
              if (item.name) {
                const columnName = item.name;
                if (item.text || (item.documents && item.documents.length > 0)) {
                  // Convert text to HTML format for DocumentManager
                  const textResponse = item.text ? { html: item.text, text: item.text } : undefined;

                  // Convert documents to DocumentManager format
                  const documents = item.documents ? item.documents.map((doc: any, idx: number) => ({
                    id: `loaded_${columnName}_${idx}`,
                    type: doc.url ? 'linked' as const : 'uploaded' as const,
                    name: doc.name || '',
                    url: doc.url || '',
                    reference: doc.reference || ''
                  })) : [];

                  responses[columnName] = {
                    textResponse,
                    documents
                  };

                  // Set localStorage for DocumentManager
                  if (item.text) {
                    localStorage.setItem(`textbox_${columnName}`, item.text);
                  }
                  if (documents.length > 0) {
                    localStorage.setItem(`documents_${columnName}`, JSON.stringify(documents));
                  }
                }
              }
            }
            if (Object.keys(responses).length > 0) {
              console.log(" Loaded deletions composition responses from Execution_PPE_CompAnal.json:", Object.keys(responses));
              setDeletionsCompositionResponses((prev) => ({ ...prev, ...responses }));
            }
          }
        }
      } catch (err) {
        console.error("Failed to load deletions composition responses from Execution_PPE_CompAnal.json", err);
      }
    } catch (error) {
      console.error("Error loading deletions analysis:", error);
      // Update debug info with error
      setDebugInfo(prev => ({
        ...prev,
        deletions: {
          ...prev.deletions,
          error: error instanceof Error ? error.message : String(error)
        }
      }));
    } finally {
      setIsLoadingDeletionsAnalysis(false);
    }
  };

  // Load composition analysis data when component mounts
  useEffect(() => {
    // Set flag to prevent auto-save during initial load
    isInitialLoadRef.current = true;

    // Load data
    Promise.all([
      loadCompositionAnalysis(),
      loadDeletionsCompositionAnalysis()
    ]).finally(() => {
      // After loading is complete, allow auto-save to work
      // Use a small delay to ensure state updates are complete
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 2000);
    });
  }, []);

  // Save specific column's additions composition response to SharePoint
  const saveAdditionsCompositionColumn = useCallback(async (columnName: string) => {
    setAdditionsSaveStatus(prev => ({ ...prev, [columnName]: 'saving' }));

    try {
      // Trigger auto-save for the textbox to ensure content is captured
      try {
        const autoSaveFunc = (window as any)[`autoSaveTextbox_${columnName}`];
        if (autoSaveFunc && typeof autoSaveFunc === 'function') {
          autoSaveFunc();
          // Small delay to ensure localStorage is updated
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.warn(`Failed to trigger auto-save for ${columnName}:`, error);
      }

      // Load existing JSON file - CRITICAL: We must load ALL existing data to preserve it
      let existingFileData: any = {};
      let dataLoaded = false;

      if (window.payroll?.run) {
        const result = await window.payroll.run("download_ppe_adl", {
          inputFiles: [],
          options: {},
        });

        if (result.ok && result.runId) {
          let finalData: any | null = null;
          let allStdout = "";
          let statusReceived = false;

          const unsubscribe = window.payroll.onProgress((payload: any) => {
            if (payload.runId !== result.runId) return;
            if (payload.stdout) {
              allStdout += String(payload.stdout);
              const extracted = extractJSONFromStdout(allStdout);
              if (extracted && typeof extracted === 'object') {
                finalData = extracted;
                dataLoaded = true;
              }
            }
            if (payload.status === "success") {
              statusReceived = true;
              if (!finalData && allStdout) {
                const extracted = extractJSONFromStdout(allStdout);
                if (extracted && typeof extracted === 'object') {
                  finalData = extracted;
                  dataLoaded = true;
                }
              }
            }
          });

          // Wait for data with longer timeout to ensure we get all data
          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              if (finalData && statusReceived) {
                clearInterval(checkInterval);
                unsubscribe();
                resolve();
              }
            }, 500);
            setTimeout(() => {
              clearInterval(checkInterval);
              unsubscribe();
              resolve();
            }, 15000); // Increased timeout to 15 seconds
          });

          if (finalData && typeof finalData === 'object') {
            // Deep clone to ensure we don't mutate the original
            existingFileData = JSON.parse(JSON.stringify(finalData));
            console.log(`[SAVE] Loaded existing file data with sections:`, Object.keys(existingFileData));
            console.log(`[SAVE] Existing Additions:`, !!existingFileData.Additions);
            console.log(`[SAVE] Existing Deletions:`, !!existingFileData.Deletions);
            console.log(`[SAVE] Existing CompositionAnalysis_Additions:`, !!existingFileData.CompositionAnalysis_Additions);
            console.log(`[SAVE] Existing CompositionAnalysis_Deletions:`, !!existingFileData.CompositionAnalysis_Deletions);
          } else {
            console.warn(`[SAVE] Warning: Could not load existing data for ${columnName}, will create new structure`);
            // Initialize with empty object to ensure we don't lose any sections
            existingFileData = {};
          }
        }
      }

      // CRITICAL: Preserve ALL existing sections BEFORE making any changes
      const preservedAdditions = existingFileData.Additions ? JSON.parse(JSON.stringify(existingFileData.Additions)) : undefined;
      const preservedDeletions = existingFileData.Deletions ? JSON.parse(JSON.stringify(existingFileData.Deletions)) : undefined;
      const preservedCompositionAnalysis_Deletions = existingFileData.CompositionAnalysis_Deletions ? JSON.parse(JSON.stringify(existingFileData.CompositionAnalysis_Deletions)) : undefined;

      // Initialize CompositionAnalysis_Additions section if it doesn't exist
      if (!existingFileData.CompositionAnalysis_Additions) {
        existingFileData.CompositionAnalysis_Additions = {};
      }

      const compositionAnalysis = existingFileData.CompositionAnalysis_Additions;

      // Log what we're about to save
      console.log(`[SAVE] About to save ${columnName}, existing composition analysis keys:`, Object.keys(compositionAnalysis));

      // Get the response for this specific column from state
      const columnResponse = additionsCompositionResponses[columnName];

      // Also check localStorage for textbox content
      const textboxContent = localStorage.getItem(`textbox_${columnName}`);
      let textResponse = columnResponse?.textResponse;
      if (textboxContent && !textResponse) {
        textResponse = {
          html: textboxContent,
          plainText: textboxContent.replace(/<[^>]*>/g, '')
        };
      }

      // Get documents from state
      const documents = columnResponse?.documents || [];

      // CRITICAL: Only update textResponse and documents fields, preserve all other fields in the column object
      if (!compositionAnalysis[columnName]) {
        compositionAnalysis[columnName] = {};
      }

      // Preserve existing column data and only update textResponse and documents
      compositionAnalysis[columnName] = {
        ...compositionAnalysis[columnName], // Preserve all existing fields
        textResponse: textResponse || null,
        documents: documents || null
      };

      // CRITICAL: Restore all preserved sections to ensure nothing is lost
      if (preservedAdditions !== undefined) {
        existingFileData.Additions = preservedAdditions;
      }
      if (preservedDeletions !== undefined) {
        existingFileData.Deletions = preservedDeletions;
      }
      if (preservedCompositionAnalysis_Deletions !== undefined) {
        existingFileData.CompositionAnalysis_Deletions = preservedCompositionAnalysis_Deletions;
      }

      // Final verification: Log the structure before saving
      console.log(`[SAVE] Final data structure for ${columnName}:`, {
        hasTextResponse: !!compositionAnalysis[columnName]?.textResponse,
        hasDocuments: compositionAnalysis[columnName]?.documents ? `${compositionAnalysis[columnName].documents.length} items` : 'none',
        allColumns: Object.keys(compositionAnalysis)
      });

      // CRITICAL: Verify we're preserving all sections before saving
      console.log(`[SAVE] File structure before save - Sections:`, Object.keys(existingFileData));
      console.log(`[SAVE] Preserving Additions:`, !!existingFileData.Additions);
      console.log(`[SAVE] Preserving Deletions:`, !!existingFileData.Deletions);
      console.log(`[SAVE] Preserving CompositionAnalysis_Additions:`, !!existingFileData.CompositionAnalysis_Additions);
      console.log(`[SAVE] Preserving CompositionAnalysis_Deletions:`, !!existingFileData.CompositionAnalysis_Deletions);

      // Save to SharePoint using uploadFile API
      if (window.sharePointAPI?.uploadFile) {
        const jsonString = JSON.stringify(existingFileData, null, 2);
        const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: "Execution_PPE_ADL.json",
          referenceValue: "Execution PPE ADL",
          folderName: "juggernaut",
          fyYear: getFY_YEAR(),
          sectionList: ["Execution_PPE_ADL"]
        });

        if (result.success) {
          console.log(` Successfully saved ${columnName} additions composition response to SharePoint`);
          setAdditionsSaveStatus(prev => ({ ...prev, [columnName]: 'saved' }));
          setTimeout(() => {
            setAdditionsSaveStatus(prev => ({ ...prev, [columnName]: 'idle' }));
          }, 2000);
          return { success: true };
        } else {
          console.error(` Failed to save ${columnName} additions composition response:`, result.error);
          setAdditionsSaveStatus(prev => ({ ...prev, [columnName]: 'error' }));
          return { success: false, error: result.error };
        }
      }

      setAdditionsSaveStatus(prev => ({ ...prev, [columnName]: 'error' }));
      return { success: false, error: "SharePoint API not available" };
    } catch (error) {
      console.error(`Error saving ${columnName} additions composition response:`, error);
      setAdditionsSaveStatus(prev => ({ ...prev, [columnName]: 'error' }));
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }, [additionsCompositionResponses]);

  // Save all composition analysis data to Execution_PPE_Substantive.json in SharePoint
  const saveAllCompositionAnalysis = useCallback(async () => {
    // Prevent multiple simultaneous saves
    if (isManualSaveInProgressRef.current) {
      console.log("Save already in progress, skipping...");
      return;
    }

    // Set flag FIRST before any operations that might trigger state updates
    isManualSaveInProgressRef.current = true;

    // Clear any pending auto-save timeouts to prevent them from running
    if (additionsSaveTimeoutRef.current) {
      clearTimeout(additionsSaveTimeoutRef.current);
      additionsSaveTimeoutRef.current = null;
    }
    if (deletionsSaveTimeoutRef.current) {
      clearTimeout(deletionsSaveTimeoutRef.current);
      deletionsSaveTimeoutRef.current = null;
    }

    setIsSavingAllComposition(true);
    setSaveAllStatus("saving");

    try {
      if (!window.sharePointAPI?.uploadFile) {
        console.error("sharePointAPI.uploadFile is not available");
        setSaveAllStatus("error");
        setIsSavingAllComposition(false);
        isManualSaveInProgressRef.current = false;
        return;
      }

      // First, trigger auto-save on all composition textboxes so the latest editor content
      // is pushed into localStorage. We read directly from localStorage, so state updates
      // from these functions won't affect the save operation.
      try {
        const allColumns = new Set<string>([
          ...Object.keys(additionsCompositionResponses),
          ...Object.keys(deletionsCompositionResponses),
          ...Object.keys(loadedAdditionsCompositionData || {}),
          ...Object.keys(loadedDeletionsCompositionData || {}),
        ]);
        allColumns.forEach((col) => {
          const autoSaveFunc = (window as any)[`autoSaveTextbox_${col}`];
          if (typeof autoSaveFunc === "function") {
            try {
              // These functions update localStorage, which is what we need
              // State updates they trigger will be blocked by isManualSaveInProgressRef
              autoSaveFunc();
            } catch (e) {
              console.warn(`Auto-save failed for composition textbox ${col}:`, e);
            }
          }
        });
        // Small delay to allow localStorage updates to settle
        await new Promise((resolve) => setTimeout(resolve, 200));
      } catch (e) {
        console.warn("Error while triggering auto-save for composition textboxes", e);
      }

      // Collect all additions composition data (keyed by chart/column name)
      // Include all columns from loaded data, not just those in responses state
      const additionsData: Record<string, any> = {};
      const allAdditionsColumns = new Set<string>([
        ...Object.keys(additionsCompositionResponses),
        ...Object.keys(loadedAdditionsCompositionData || {}),
      ]);

      for (const columnName of allAdditionsColumns) {
        const response = additionsCompositionResponses[columnName] || {};
        // Always check localStorage first (highest priority - contains latest editor content)
        const textboxContent = localStorage.getItem(`textbox_${columnName}`);
        let textResponse = response.textResponse;

        // Prioritize localStorage content if it exists
        if (textboxContent && textboxContent.trim().length > 0) {
          textResponse = {
            html: textboxContent,
            plainText: textboxContent.replace(/<[^>]*>/g, ""),
          };
        } else if (!textResponse && response.textResponse) {
          // Fallback to state if localStorage is empty
          textResponse = response.textResponse;
        }

        // Build documents array (excluding textbox type) - same format as planning
        const fileDocuments = response.documents && Array.isArray(response.documents)
          ? response.documents.filter((doc: any) => doc.type !== 'textbox').map((doc: any) => ({
            name: doc.name,
            type: doc.type,
            url: doc.url,
            reference: doc.reference
          }))
          : [];

        additionsData[columnName] = {
          textResponse: textResponse || null,
          documents: fileDocuments.length > 0 ? fileDocuments : null,
        };
      }

      // Collect all deletions composition data (keyed by chart/column name)
      // Include all columns from loaded data, not just those in responses state
      const deletionsData: Record<string, any> = {};
      const allDeletionsColumns = new Set<string>([
        ...Object.keys(deletionsCompositionResponses),
        ...Object.keys(loadedDeletionsCompositionData || {}),
      ]);

      for (const columnName of allDeletionsColumns) {
        const response = deletionsCompositionResponses[columnName] || {};
        // Always check localStorage first (highest priority - contains latest editor content)
        const textboxContent = localStorage.getItem(`textbox_${columnName}`);
        let textResponse = response.textResponse;

        // Prioritize localStorage content if it exists
        if (textboxContent && textboxContent.trim().length > 0) {
          textResponse = {
            html: textboxContent,
            plainText: textboxContent.replace(/<[^>]*>/g, ""),
          };
        } else if (!textResponse && response.textResponse) {
          // Fallback to state if localStorage is empty
          textResponse = response.textResponse;
        }

        // Build documents array (excluding textbox type) - same format as planning
        const fileDocuments = response.documents && Array.isArray(response.documents)
          ? response.documents.filter((doc: any) => doc.type !== 'textbox').map((doc: any) => ({
            name: doc.name,
            type: doc.type,
            url: doc.url,
            reference: doc.reference
          }))
          : [];

        deletionsData[columnName] = {
          textResponse: textResponse || null,
          documents: fileDocuments.length > 0 ? fileDocuments : null,
        };
      }

      // Final JSON structure for Execution_PPE_Substantive.json
      const fileData = {
        CompositionAnalysis: {
          Additions: additionsData,
          Deletions: deletionsData,
        },
      };

      const jsonString = JSON.stringify(fileData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const result = await window.sharePointAPI.uploadFile({
        fileContent: base64Content,
        fileName: "Execution_PPE_Substantive.json",
        referenceValue: "Execution PPE Substantive",
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: ["Execution_PPE_Substantive"],
      });

      if (result.success) {
        console.log(" Successfully saved all composition analysis data to Execution_PPE_Substantive.json");
        setSaveAllStatus("saved");
        setTimeout(() => {
          setSaveAllStatus("idle");
        }, 3000);
      } else {
        const errorMessage = result.error || "Failed to save composition analysis data";
        console.error(" Failed to save composition analysis data:", errorMessage);
        setSaveAllStatus("error");
        setSaveError(errorMessage);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      console.error("Error saving all composition analysis data:", errorMessage);
      setSaveAllStatus("error");
      setSaveError(errorMessage);
    } finally {
      setIsSavingAllComposition(false);
      // Reset flag after a longer delay to ensure all state updates from autoSaveTextbox have settled
      // and to prevent auto-save from triggering immediately after manual save completes
      setTimeout(() => {
        isManualSaveInProgressRef.current = false;
      }, 2000); // Increased delay to 2 seconds to prevent immediate re-triggering
    }
  }, [additionsCompositionResponses, deletionsCompositionResponses, loadedAdditionsCompositionData, loadedDeletionsCompositionData]);

  // Save Additions Composition Analysis to JSON file
  const saveAdditionsCompositionAnalysis = useCallback(async () => {
    setIsSavingAdditionsComposition(true);
    setSaveAdditionsCompositionStatus('saving');
    setSaveAdditionsCompositionError(null);

    try {
      // Get all unique column names from both sources
      const allColumnNames = new Set<string>();

      // Add columns from additionsCompositionResponses
      Object.keys(additionsCompositionResponses).forEach(col => allColumnNames.add(col));

      // Add columns from loadedAdditionsCompositionData
      Object.keys(loadedAdditionsCompositionData).forEach(col => allColumnNames.add(col));

      // Trigger auto-save for all textboxes to ensure content is captured
      for (const columnName of Array.from(allColumnNames)) {
        try {
          const autoSaveFunc = (window as any)[`autoSaveTextbox_${columnName}`];
          if (autoSaveFunc && typeof autoSaveFunc === 'function') {
            autoSaveFunc();
          }
        } catch (error) {
          console.warn(`Failed to trigger auto-save for ${columnName}:`, error);
        }
      }

      // Small delay to ensure localStorage is updated
      await new Promise(resolve => setTimeout(resolve, 200));

      // Collect all additions composition data
      const additionsData: Array<{
        name: string;
        text: string;
        documents: Array<{
          name: string;
          url: string;
          reference: string;
        }>;
      }> = [];

      // Process each column
      for (const columnName of Array.from(allColumnNames)) {
        const response = additionsCompositionResponses[columnName] || {};
        const loadedData = loadedAdditionsCompositionData[columnName] || {};

        // Get text response - extract text from HTML if needed
        // First check localStorage (most recent), then state, then loaded data
        let textValue = '';

        // Check localStorage first (most recent content)
        try {
          const localStorageKey = `textbox_${columnName}`;
          const localStorageContent = localStorage.getItem(localStorageKey);
          if (localStorageContent) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = localStorageContent;
            textValue = tempDiv.textContent || tempDiv.innerText || '';
          }
        } catch (error) {
          console.warn(`Failed to read from localStorage for ${columnName}:`, error);
        }

        // If no localStorage content, check state
        if (!textValue) {
          if (response.textResponse?.html) {
            // Extract plain text from HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = response.textResponse.html;
            textValue = tempDiv.textContent || tempDiv.innerText || '';
          } else if (response.textResponse?.text) {
            textValue = response.textResponse.text;
          } else if (loadedData.textResponse?.html) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = loadedData.textResponse.html;
            textValue = tempDiv.textContent || tempDiv.innerText || '';
          } else if (loadedData.textResponse?.text) {
            textValue = loadedData.textResponse.text;
          }
        }

        // Get documents - exclude textbox type
        const documents: Array<{ name: string; url: string; reference: string }> = [];
        const responseDocs = response.documents || [];
        const loadedDocs = loadedData.documents || [];

        // Also check localStorage for documents
        let localStorageDocs: any[] = [];
        try {
          const localStorageKey = `documents_${columnName}`;
          const localStorageContent = localStorage.getItem(localStorageKey);
          if (localStorageContent) {
            localStorageDocs = JSON.parse(localStorageContent);
          }
        } catch (error) {
          console.warn(`Failed to read documents from localStorage for ${columnName}:`, error);
        }

        // Combine and deduplicate documents
        const allDocs = [...responseDocs, ...loadedDocs, ...localStorageDocs];
        const seenDocs = new Set<string>();

        for (const doc of allDocs) {
          // Skip textbox documents
          if (doc.type === 'textbox') continue;

          // Skip if we've already seen this document (by id or name+url combination)
          const docKey = doc.id || `${doc.name}_${doc.url}`;
          if (seenDocs.has(docKey)) continue;
          seenDocs.add(docKey);

          // For uploaded files, url and reference should already be set
          // For linked files, they come from SharePoint
          if (doc.url && doc.name) {
            documents.push({
              name: doc.name,
              url: doc.url,
              reference: doc.reference || ''
            });
          }
        }

        // Only add entry if there's at least text or documents
        if (textValue.trim() || documents.length > 0) {
          additionsData.push({
            name: columnName,
            text: textValue,
            documents: documents
          });
        }
      }

      // Try to download existing file first
      let existingJsonData: any = null;
      const fileName = "Execution_PPE_CompAnal.json";
      const folderName = "juggernaut";
      const fyYear = getFY_YEAR();

      try {
        // Use payroll.run to download the file if it exists
        if (window.payroll?.run) {
          const downloadResult = await window.payroll.run("download_ppe_comp_anal", {
            inputFiles: [],
            options: {},
          });

          if (downloadResult.ok && downloadResult.runId) {
            let finalData: any | null = null;
            let allStdout = "";
            let statusReceived = false;

            const unsubscribe = window.payroll.onProgress((payload: any) => {
              if (payload.runId !== downloadResult.runId) return;
              if (payload.stdout) {
                allStdout += String(payload.stdout);
                try {
                  // Try to parse the JSON response
                  const jsonMatch = allStdout.match(/\{[\s\S]*\}/);
                  if (jsonMatch) {
                    const parsed = JSON.parse(jsonMatch[0]);
                    if (parsed && typeof parsed === 'object' && parsed.success && parsed.data) {
                      finalData = parsed.data;
                    } else if (parsed && typeof parsed === 'object' && !parsed.success) {
                      // File doesn't exist or error, that's okay
                      finalData = null;
                    }
                  }
                } catch (e) {
                  // Ignore parse errors, will try again on next stdout
                }
              }
              if (payload.status === "success") {
                statusReceived = true;
              }
            });

            await new Promise<void>((resolve) => {
              const checkInterval = setInterval(() => {
                if (statusReceived) {
                  clearInterval(checkInterval);
                  unsubscribe();
                  resolve();
                }
              }, 500);
              setTimeout(() => {
                clearInterval(checkInterval);
                unsubscribe();
                resolve();
              }, 10000);
            });

            if (finalData && typeof finalData === 'object' && Object.keys(finalData).length > 0) {
              existingJsonData = finalData;
            }
          }
        }
      } catch (error) {
        console.warn("Could not download existing file, will create new one:", error);
      }

      // Create or update JSON structure
      let jsonData: any;
      if (existingJsonData && typeof existingJsonData === 'object') {
        // Update existing file - merge Additions, preserve other sections
        jsonData = {
          Additions: additionsData,
          Deletions: existingJsonData.Deletions || [],
          CWIP: existingJsonData.CWIP || [],
          PPE: existingJsonData.PPE || []
        };
      } else {
        // Create new file
        jsonData = {
          Additions: additionsData,
          Deletions: [],
          CWIP: [],
          PPE: []
        };
      }

      // Upload to SharePoint using uploadFile API
      if (window.sharePointAPI?.uploadFile) {
        const jsonString = JSON.stringify(jsonData, null, 2);
        const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: fileName,
          referenceValue: "Execution PPE Composition Analysis - Additions",
          folderName: folderName,
          fyYear: fyYear,
          sectionList: ["Execution_PPE_CompAnal"]
        });

        if (result.success) {
          console.log(" Successfully saved Additions Composition Analysis to Execution_PPE_CompAnal.json");
          setSaveAdditionsCompositionStatus('saved');
          setTimeout(() => {
            setSaveAdditionsCompositionStatus('idle');
          }, 3000);
        } else {
          console.error(" Failed to save Additions Composition Analysis:", result.error);
          setSaveAdditionsCompositionStatus('error');
          setSaveAdditionsCompositionError(result.error || 'Unknown error');
        }
      } else {
        throw new Error("SharePoint API not available");
      }
    } catch (error) {
      console.error("Error saving Additions Composition Analysis:", error);
      setSaveAdditionsCompositionStatus('error');
      setSaveAdditionsCompositionError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsSavingAdditionsComposition(false);
    }
  }, [additionsCompositionResponses, loadedAdditionsCompositionData]);

  // Save Deletions Composition Analysis to JSON file
  const saveDeletionsCompositionAnalysis = useCallback(async () => {
    setIsSavingDeletionsComposition(true);
    setSaveDeletionsCompositionStatus('saving');
    setSaveDeletionsCompositionError(null);

    try {
      // Get all unique column names from both sources
      const allColumnNames = new Set<string>();

      // Add columns from deletionsCompositionResponses
      Object.keys(deletionsCompositionResponses).forEach(col => allColumnNames.add(col));

      // Add columns from loadedDeletionsCompositionData
      Object.keys(loadedDeletionsCompositionData).forEach(col => allColumnNames.add(col));

      // Trigger auto-save for all textboxes to ensure content is captured
      for (const columnName of Array.from(allColumnNames)) {
        try {
          const autoSaveFunc = (window as any)[`autoSaveTextbox_${columnName}`];
          if (autoSaveFunc && typeof autoSaveFunc === 'function') {
            autoSaveFunc();
          }
        } catch (error) {
          console.warn(`Failed to trigger auto-save for ${columnName}:`, error);
        }
      }

      // Small delay to ensure localStorage is updated
      await new Promise(resolve => setTimeout(resolve, 200));

      // Collect all deletions composition data
      const deletionsData: Array<{
        name: string;
        text: string;
        documents: Array<{
          name: string;
          url: string;
          reference: string;
        }>;
      }> = [];

      // Process each column
      for (const columnName of Array.from(allColumnNames)) {
        const response = deletionsCompositionResponses[columnName] || {};
        const loadedData = loadedDeletionsCompositionData[columnName] || {};

        // Get text response - extract text from HTML if needed
        // First check localStorage (most recent), then state, then loaded data
        let textValue = '';

        // Check localStorage first (most recent content)
        try {
          const localStorageKey = `textbox_${columnName}`;
          const localStorageContent = localStorage.getItem(localStorageKey);
          if (localStorageContent) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = localStorageContent;
            textValue = tempDiv.textContent || tempDiv.innerText || '';
          }
        } catch (error) {
          console.warn(`Failed to read from localStorage for ${columnName}:`, error);
        }

        // If no localStorage content, check state
        if (!textValue) {
          if (response.textResponse?.html) {
            // Extract plain text from HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = response.textResponse.html;
            textValue = tempDiv.textContent || tempDiv.innerText || '';
          } else if (response.textResponse?.text) {
            textValue = response.textResponse.text;
          } else if (loadedData.textResponse?.html) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = loadedData.textResponse.html;
            textValue = tempDiv.textContent || tempDiv.innerText || '';
          } else if (loadedData.textResponse?.text) {
            textValue = loadedData.textResponse.text;
          }
        }

        // Get documents - exclude textbox type
        const documents: Array<{ name: string; url: string; reference: string }> = [];
        const responseDocs = response.documents || [];
        const loadedDocs = loadedData.documents || [];

        // Also check localStorage for documents
        let localStorageDocs: any[] = [];
        try {
          const localStorageKey = `documents_${columnName}`;
          const localStorageContent = localStorage.getItem(localStorageKey);
          if (localStorageContent) {
            localStorageDocs = JSON.parse(localStorageContent);
          }
        } catch (error) {
          console.warn(`Failed to read documents from localStorage for ${columnName}:`, error);
        }

        // Combine and deduplicate documents
        const allDocs = [...responseDocs, ...loadedDocs, ...localStorageDocs];
        const seenDocs = new Set<string>();

        for (const doc of allDocs) {
          // Skip textbox documents
          if (doc.type === 'textbox') continue;

          // Skip if we've already seen this document (by id or name+url combination)
          const docKey = doc.id || `${doc.name}_${doc.url}`;
          if (seenDocs.has(docKey)) continue;
          seenDocs.add(docKey);

          // For uploaded files, url and reference should already be set
          // For linked files, they come from SharePoint
          if (doc.url && doc.name) {
            documents.push({
              name: doc.name,
              url: doc.url,
              reference: doc.reference || ''
            });
          }
        }

        // Only add entry if there's at least text or documents
        if (textValue.trim() || documents.length > 0) {
          deletionsData.push({
            name: columnName,
            text: textValue,
            documents: documents
          });
        }
      }

      // Try to download existing file first
      let existingJsonData: any = null;
      const fileName = "Execution_PPE_CompAnal.json";
      const folderName = "juggernaut";
      const fyYear = getFY_YEAR();

      try {
        // Use payroll.run to download the file if it exists
        if (window.payroll?.run) {
          const downloadResult = await window.payroll.run("download_ppe_comp_anal", {
            inputFiles: [],
            options: {},
          });

          if (downloadResult.ok && downloadResult.runId) {
            let finalData: any | null = null;
            let allStdout = "";
            let statusReceived = false;

            const unsubscribe = window.payroll.onProgress((payload: any) => {
              if (payload.runId !== downloadResult.runId) return;
              if (payload.stdout) {
                allStdout += String(payload.stdout);
                try {
                  // Try to parse the JSON response
                  const jsonMatch = allStdout.match(/\{[\s\S]*\}/);
                  if (jsonMatch) {
                    const parsed = JSON.parse(jsonMatch[0]);
                    if (parsed && typeof parsed === 'object' && parsed.success && parsed.data) {
                      finalData = parsed.data;
                    } else if (parsed && typeof parsed === 'object' && !parsed.success) {
                      // File doesn't exist or error, that's okay
                      finalData = null;
                    }
                  }
                } catch (e) {
                  // Ignore parse errors, will try again on next stdout
                }
              }
              if (payload.status === "success") {
                statusReceived = true;
              }
            });

            await new Promise<void>((resolve) => {
              const checkInterval = setInterval(() => {
                if (statusReceived) {
                  clearInterval(checkInterval);
                  unsubscribe();
                  resolve();
                }
              }, 500);
              setTimeout(() => {
                clearInterval(checkInterval);
                unsubscribe();
                resolve();
              }, 10000);
            });

            if (finalData && typeof finalData === 'object' && Object.keys(finalData).length > 0) {
              existingJsonData = finalData;
            }
          }
        }
      } catch (error) {
        console.warn("Could not download existing file, will create new one:", error);
      }

      // Create or update JSON structure - only update Deletions section
      let jsonData: any;
      if (existingJsonData && typeof existingJsonData === 'object') {
        // Update existing file - merge Deletions, preserve other sections
        jsonData = {
          Additions: existingJsonData.Additions || [],
          Deletions: deletionsData,
          CWIP: existingJsonData.CWIP || [],
          PPE: existingJsonData.PPE || []
        };
      } else {
        // Create new file
        jsonData = {
          Additions: [],
          Deletions: deletionsData,
          CWIP: [],
          PPE: []
        };
      }

      // Upload to SharePoint using uploadFile API
      if (window.sharePointAPI?.uploadFile) {
        const jsonString = JSON.stringify(jsonData, null, 2);
        const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: fileName,
          referenceValue: "Execution PPE Composition Analysis - Deletions",
          folderName: folderName,
          fyYear: fyYear,
          sectionList: ["Execution_PPE_CompAnal"]
        });

        if (result.success) {
          console.log(" Successfully saved Deletions Composition Analysis to Execution_PPE_CompAnal.json");
          setSaveDeletionsCompositionStatus('saved');
          setTimeout(() => {
            setSaveDeletionsCompositionStatus('idle');
          }, 3000);
        } else {
          console.error(" Failed to save Deletions Composition Analysis:", result.error);
          setSaveDeletionsCompositionStatus('error');
          setSaveDeletionsCompositionError(result.error || 'Unknown error');
        }
      } else {
        throw new Error("SharePoint API not available");
      }
    } catch (error) {
      console.error("Error saving Deletions Composition Analysis:", error);
      setSaveDeletionsCompositionStatus('error');
      setSaveDeletionsCompositionError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsSavingDeletionsComposition(false);
    }
  }, [deletionsCompositionResponses, loadedDeletionsCompositionData]);

  // Auto-save helper for additions composition responses.
  // We no longer write to Execution_PPE_ADL.json from here; persistence is handled
  // centrally by saveAllCompositionAnalysis to Execution_PPE_Substantive.json.
  const saveAdditionsCompositionResponses = useCallback(async () => {
    try {
      await saveAllCompositionAnalysis();
    } catch (error) {
      console.error("Error in saveAdditionsCompositionResponses (delegated to saveAllCompositionAnalysis):", error);
    }
  }, [saveAllCompositionAnalysis]);

  // Save specific column's deletions composition response to SharePoint
  const saveDeletionsCompositionColumn = useCallback(async (columnName: string) => {
    setDeletionsSaveStatus(prev => ({ ...prev, [columnName]: 'saving' }));

    try {
      // Trigger auto-save for the textbox to ensure content is captured
      try {
        const autoSaveFunc = (window as any)[`autoSaveTextbox_${columnName}`];
        if (autoSaveFunc && typeof autoSaveFunc === 'function') {
          autoSaveFunc();
          // Small delay to ensure localStorage is updated
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.warn(`Failed to trigger auto-save for ${columnName}:`, error);
      }

      // Load existing JSON file - CRITICAL: We must load ALL existing data to preserve it
      let existingFileData: any = {};
      let dataLoaded = false;

      if (window.payroll?.run) {
        const result = await window.payroll.run("download_ppe_adl", {
          inputFiles: [],
          options: {},
        });

        if (result.ok && result.runId) {
          let finalData: any | null = null;
          let allStdout = "";
          let statusReceived = false;

          const unsubscribe = window.payroll.onProgress((payload: any) => {
            if (payload.runId !== result.runId) return;
            if (payload.stdout) {
              allStdout += String(payload.stdout);
              const extracted = extractJSONFromStdout(allStdout);
              if (extracted && typeof extracted === 'object') {
                finalData = extracted;
                dataLoaded = true;
              }
            }
            if (payload.status === "success") {
              statusReceived = true;
              if (!finalData && allStdout) {
                const extracted = extractJSONFromStdout(allStdout);
                if (extracted && typeof extracted === 'object') {
                  finalData = extracted;
                  dataLoaded = true;
                }
              }
            }
          });

          // Wait for data with longer timeout to ensure we get all data
          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              if (finalData && statusReceived) {
                clearInterval(checkInterval);
                unsubscribe();
                resolve();
              }
            }, 500);
            setTimeout(() => {
              clearInterval(checkInterval);
              unsubscribe();
              resolve();
            }, 15000); // Increased timeout to 15 seconds
          });

          if (finalData && typeof finalData === 'object') {
            // Deep clone to ensure we don't mutate the original
            existingFileData = JSON.parse(JSON.stringify(finalData));
            console.log(`[SAVE] Loaded existing file data with sections:`, Object.keys(existingFileData));
            console.log(`[SAVE] Existing Additions:`, !!existingFileData.Additions);
            console.log(`[SAVE] Existing Deletions:`, !!existingFileData.Deletions);
            console.log(`[SAVE] Existing CompositionAnalysis_Additions:`, !!existingFileData.CompositionAnalysis_Additions);
            console.log(`[SAVE] Existing CompositionAnalysis_Deletions:`, !!existingFileData.CompositionAnalysis_Deletions);
          } else {
            console.warn(`[SAVE] Warning: Could not load existing data for ${columnName}, will create new structure`);
            // Initialize with empty object to ensure we don't lose any sections
            existingFileData = {};
          }
        }
      }

      // Initialize CompositionAnalysis_Deletions section if it doesn't exist
      if (!existingFileData.CompositionAnalysis_Deletions) {
        existingFileData.CompositionAnalysis_Deletions = {};
      }

      const compositionAnalysis = existingFileData.CompositionAnalysis_Deletions;

      // Log what we're about to save
      console.log(`[SAVE] About to save ${columnName}, existing composition analysis keys:`, Object.keys(compositionAnalysis));

      // Get the response for this specific column from state
      const columnResponse = deletionsCompositionResponses[columnName];

      // Also check localStorage for textbox content
      const textboxContent = localStorage.getItem(`textbox_${columnName}`);
      let textResponse = columnResponse?.textResponse;
      if (textboxContent && !textResponse) {
        textResponse = {
          html: textboxContent,
          plainText: textboxContent.replace(/<[^>]*>/g, '')
        };
      }

      // Get documents from state
      const documents = columnResponse?.documents || [];

      // CRITICAL: Only update textResponse and documents fields, preserve all other fields in the column object
      if (!compositionAnalysis[columnName]) {
        compositionAnalysis[columnName] = {};
      }

      // Preserve existing column data and only update textResponse and documents
      compositionAnalysis[columnName] = {
        ...compositionAnalysis[columnName], // Preserve all existing fields
        textResponse: textResponse || null,
        documents: documents || null
      };

      // Final verification: Log the structure before saving
      console.log(`[SAVE] Final data structure for ${columnName}:`, {
        hasTextResponse: !!compositionAnalysis[columnName]?.textResponse,
        hasDocuments: compositionAnalysis[columnName]?.documents ? `${compositionAnalysis[columnName].documents.length} items` : 'none',
        allColumns: Object.keys(compositionAnalysis)
      });

      // CRITICAL: Verify we're preserving all sections before saving
      console.log(`[SAVE] File structure before save - Sections:`, Object.keys(existingFileData));
      console.log(`[SAVE] Preserving Additions:`, !!existingFileData.Additions);
      console.log(`[SAVE] Preserving Deletions:`, !!existingFileData.Deletions);
      console.log(`[SAVE] Preserving CompositionAnalysis_Additions:`, !!existingFileData.CompositionAnalysis_Additions);
      console.log(`[SAVE] Preserving CompositionAnalysis_Deletions:`, !!existingFileData.CompositionAnalysis_Deletions);

      // Save to SharePoint using uploadFile API
      if (window.sharePointAPI?.uploadFile) {
        const jsonString = JSON.stringify(existingFileData, null, 2);
        const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const result = await window.sharePointAPI.uploadFile({
        fileContent: base64Content,
        fileName: "Execution_PPE_ADL.json",
        referenceValue: "Execution PPE ADL",
        folderName: "juggernaut",
        fyYear: getFY_YEAR(),
        sectionList: ["Execution_PPE_ADL"]
      });

        if (result.success) {
          console.log(` Successfully saved ${columnName} deletions composition response to SharePoint`);
          setDeletionsSaveStatus(prev => ({ ...prev, [columnName]: 'saved' }));
          setTimeout(() => {
            setDeletionsSaveStatus(prev => ({ ...prev, [columnName]: 'idle' }));
          }, 2000);
          return { success: true };
        } else {
          console.error(` Failed to save ${columnName} deletions composition response:`, result.error);
          setDeletionsSaveStatus(prev => ({ ...prev, [columnName]: 'error' }));
          return { success: false, error: result.error };
        }
      }

      setDeletionsSaveStatus(prev => ({ ...prev, [columnName]: 'error' }));
      return { success: false, error: "SharePoint API not available" };
    } catch (error) {
      console.error(`Error saving ${columnName} deletions composition response:`, error);
      setDeletionsSaveStatus(prev => ({ ...prev, [columnName]: 'error' }));
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }, [deletionsCompositionResponses]);

  // Auto-save helper for deletions composition responses.
  // We no longer write to Execution_PPE_ADL.json from here; persistence is handled
  // centrally by saveAllCompositionAnalysis to Execution_PPE_Substantive.json.
  const saveDeletionsCompositionResponses = useCallback(async () => {
    try {
      await saveAllCompositionAnalysis();
    } catch (error) {
      console.error("Error in saveDeletionsCompositionResponses (delegated to saveAllCompositionAnalysis):", error);
    }
  }, [saveAllCompositionAnalysis]);

  // Auto-save additions composition responses with debounce
  useEffect(() => {
    // Skip auto-save during initial load or manual save
    if (isInitialLoadRef.current || isManualSaveInProgressRef.current) {
      return;
    }

    if (Object.keys(additionsCompositionResponses).length === 0) return;

    // Clear existing timeout
    if (additionsSaveTimeoutRef.current) {
      clearTimeout(additionsSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (5 seconds after last change)
    additionsSaveTimeoutRef.current = setTimeout(() => {
      // Double-check flag before executing
      if (!isManualSaveInProgressRef.current) {
        saveAdditionsCompositionResponses().catch(error => {
          console.error("Auto-save failed for additions composition responses:", error);
        });
      }
    }, 5000);

    return () => {
      if (additionsSaveTimeoutRef.current) {
        clearTimeout(additionsSaveTimeoutRef.current);
      }
    };
  }, [additionsCompositionResponses, saveAdditionsCompositionResponses]);

  // Auto-save deletions composition responses with debounce
  useEffect(() => {
    // Skip auto-save during initial load or manual save
    if (isInitialLoadRef.current || isManualSaveInProgressRef.current) {
      return;
    }

    if (Object.keys(deletionsCompositionResponses).length === 0) return;

    // Clear existing timeout
    if (deletionsSaveTimeoutRef.current) {
      clearTimeout(deletionsSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (5 seconds after last change)
    deletionsSaveTimeoutRef.current = setTimeout(() => {
      // Double-check flag before executing
      if (!isManualSaveInProgressRef.current) {
        saveDeletionsCompositionResponses().catch(error => {
          console.error("Auto-save failed for deletions composition responses:", error);
        });
      }
    }, 5000);

    return () => {
      if (deletionsSaveTimeoutRef.current) {
        clearTimeout(deletionsSaveTimeoutRef.current);
      }
    };
  }, [deletionsCompositionResponses, saveDeletionsCompositionResponses]);

  // Run Deletions Composition Analysis
  const runDeletionsCompositionAnalysis = async () => {
    if (deletionsCompositionColumns.size === 0 || !deletionsCompositionAmountCol) return;

    setIsRunningDeletionsCompositionAnalysis(true);
    try {
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_ppe_deletions_composition_analysis", {
          inputFiles: [],
          options: {
            group_columns: Array.from(deletionsCompositionColumns),
            amount_column: deletionsCompositionAmountCol,
          },
        });

        if (result.ok && result.runId) {
          const unsubscribe = window.payroll.onProgress((payload: any) => {
            if (payload.runId !== result.runId) return;

            if (payload.status === "success") {
              try {
                const lines = String(payload.stdout || '').split('\n');
                const jsonLine = lines.find((l: string) => l.trim().startsWith('{') && l.includes('success'));
                if (jsonLine) {
                  const parsed = JSON.parse(jsonLine);
                  if (parsed.success) {
                    console.log("Deletions Composition Analysis completed:", parsed);
                  }
                }
              } catch (e) {
                console.error("Error parsing result:", e);
              }
              setIsRunningDeletionsCompositionAnalysis(false);
              unsubscribe();
            } else if (payload.status === "error") {
              console.error("Deletions composition analysis error:", payload.error);
              setIsRunningDeletionsCompositionAnalysis(false);
              unsubscribe();
            }
          });
        } else {
          setIsRunningDeletionsCompositionAnalysis(false);
        }
      } else {
        setIsRunningDeletionsCompositionAnalysis(false);
      }
    } catch (error) {
      console.error("Error running deletions composition analysis:", error);
      setIsRunningDeletionsCompositionAnalysis(false);
    }
  };

  // Run composition analysis
  const runCompositionAnalysis = async () => {
    setIsRunningCompositionAnalysis(true);
    try {
      const additionsResults: Record<string, any> = {};
      const deletionsResults: Record<string, any> = {};
      let additionsCompleted = false;
      let deletionsCompleted = false;

      // Helper to extract JSON from stdout
      const extractJSON = (stdout: string): any => {
        try {
          const lines = String(stdout || '').split('\n');
          const jsonLine = lines.find((l: string) => l.trim().startsWith('{') && l.includes('success'));
          if (jsonLine) {
            return JSON.parse(jsonLine);
          }
        } catch (e) {
          console.error("Error extracting JSON:", e);
        }
        return null;
      };

      // Analyze Additions - call backend
      if (additionsCompositionColumns.size > 0 && additionsFile && additionsAmountCol) {
        if (window.payroll?.run) {
          const result = await window.payroll.run("execute_ppe_composition_analysis", {
            inputFiles: [],
            options: {
              file_name: additionsFile,
              columns: Array.from(additionsCompositionColumns),
              amount_column: additionsAmountCol,
              analysis_type: "additions",
            },
          });

          if (result.ok && result.runId) {
            const unsubscribe = window.payroll.onProgress((payload: any) => {
              if (payload.runId !== result.runId) return;

              if (payload.status === "success") {
                const parsed = extractJSON(payload.stdout);
                if (parsed && parsed.success && parsed.data) {
                  Object.entries(parsed.data).forEach(([column, analysis]: [string, any]) => {
                    // Convert rows, counts, and amounts to chartData format
                    const chartData = analysis.rows?.map((row: string, index: number) => ({
                      name: row,
                      value: analysis.counts?.[index] || 0,
                      amount: analysis.amounts?.[index] || 0
                    })) || [];

                    additionsResults[column] = {
                      ...analysis,
                      chartData: chartData
                    };
                  });
                  additionsCompleted = true;
                  setAdditionsCompositionResults({ ...additionsResults });

                  if (additionsCompleted && (deletionsCompositionColumns.size === 0 || deletionsCompleted)) {
                    setIsRunningCompositionAnalysis(false);
                  }
                }
                unsubscribe();
              } else if (payload.status === "error") {
                console.error("Additions composition analysis error:", payload.error);
                additionsCompleted = true;
                if (additionsCompleted && (deletionsCompositionColumns.size === 0 || deletionsCompleted)) {
                  setIsRunningCompositionAnalysis(false);
                }
                unsubscribe();
              }
            });
          }
        }
      } else {
        additionsCompleted = true;
      }

      // Analyze Deletions - call backend
      if (deletionsCompositionColumns.size > 0 && deletionsFile && deletionsAmountCol) {
        if (window.payroll?.run) {
          const result = await window.payroll.run("execute_ppe_composition_analysis", {
            inputFiles: [],
            options: {
              file_name: deletionsFile,
              columns: Array.from(deletionsCompositionColumns),
              amount_column: deletionsAmountCol,
              analysis_type: "deletions",
            },
          });

          if (result.ok && result.runId) {
            const unsubscribe = window.payroll.onProgress((payload: any) => {
              if (payload.runId !== result.runId) return;

              if (payload.status === "success") {
                const parsed = extractJSON(payload.stdout);
                if (parsed && parsed.success && parsed.data) {
                  Object.entries(parsed.data).forEach(([column, analysis]: [string, any]) => {
                    // Convert rows, counts, and amounts to chartData format
                    const chartData = analysis.rows?.map((row: string, index: number) => ({
                      name: row,
                      value: analysis.counts?.[index] || 0,
                      amount: analysis.amounts?.[index] || 0
                    })) || [];

                    deletionsResults[column] = {
                      ...analysis,
                      chartData: chartData
                    };
                  });
                  deletionsCompleted = true;
                  setDeletionsCompositionResults({ ...deletionsResults });

                  if (deletionsCompleted && (additionsCompositionColumns.size === 0 || additionsCompleted)) {
                    setIsRunningCompositionAnalysis(false);
                  }
                }
                unsubscribe();
              } else if (payload.status === "error") {
                console.error("Deletions composition analysis error:", payload.error);
                deletionsCompleted = true;
                if (deletionsCompleted && (additionsCompositionColumns.size === 0 || additionsCompleted)) {
                  setIsRunningCompositionAnalysis(false);
                }
                unsubscribe();
              }
            });
          }
        }
      } else {
        deletionsCompleted = true;
      }

      // If no analysis to run, set completed immediately
      if (additionsCompositionColumns.size === 0 && deletionsCompositionColumns.size === 0) {
        setIsRunningCompositionAnalysis(false);
      }
    } catch (error) {
      console.error("Error running composition analysis:", error);
      setIsRunningCompositionAnalysis(false);
    }
  };


  const canRun = () =>
    additionsFile && deletionsFile &&
    additionsDateCol && additionsAmountCol &&
    deletionsDateCol && deletionsAmountCol;

  const runADL = async () => {
    if (!canRun()) return;

    // Save column mappings to file usage reference
    const entries: FileUsageEntry[] = [];
    const additionsFileName = additionsFile.includes(' (') ? additionsFile.split(' (')[0] : additionsFile;
    const deletionsFileName = deletionsFile.includes(' (') ? deletionsFile.split(' (')[0] : deletionsFile;
    entries.push({ "Search for Files - Additions": additionsFileName });
    entries.push({ "Search for Files - Deletions": deletionsFileName });
    const columnMapping: Record<string, any> = {
      additions: {
        dateColumn: additionsDateCol,
        amountColumn: additionsAmountCol
      },
      deletions: {
        dateColumn: deletionsDateCol,
        amountColumn: deletionsAmountCol
      }
    };
    entries.push({ "Column Mapping": JSON.stringify(columnMapping) });
    if (entries.length > 0) {
      await updateProcedureFileUsageForWorkspace("Property, Plant & Equipment", "Additions & Deletions Analysis", entries);
      await loadFileUsageReference();
    }

    setProcessingStatus("running");
    setProgress(0);
    setFileUrl("");

    try {
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_ppe_adl", {
          inputFiles: [],
          options: {
            file_additions: additionsFile,
            file_deletions: deletionsFile,
            col_add: [additionsDateCol, additionsAmountCol],
            col_del: [deletionsDateCol, deletionsAmountCol],
          },
        });

        if (result.ok) {
          setProcessingStatus("completed");
        } else {
          setProcessingStatus("error");
        }
      } else {
        setProcessingStatus("error");
      }
    } catch (e) {
      setProcessingStatus("error");
    }
  };

  const generateDummyTableData = (columns: ExcelColumn[], count: number = 10) => {
    return Array.from({ length: count }, (_, idx) => {
      const row: any = {};
      columns.forEach(col => {
        const colName = col.name.toLowerCase();
        if (colName.includes('date')) {
          row[col.name] = new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toLocaleDateString();
        } else if (colName.includes('amount') || colName.includes('cost') || colName.includes('value')) {
          row[col.name] = (Math.random() * 1000000).toFixed(2);
        } else if (colName.includes('code') || colName.includes('id')) {
          row[col.name] = `CODE-${String(idx + 1).padStart(4, '0')}`;
        } else if (colName.includes('location')) {
          const locations = ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Hyderabad'];
          row[col.name] = locations[Math.floor(Math.random() * locations.length)];
        } else if (colName.includes('category') || colName.includes('type')) {
          const categories = ['Building', 'Machinery', 'Vehicle', 'Equipment', 'Furniture'];
          row[col.name] = categories[Math.floor(Math.random() * categories.length)];
        } else {
          row[col.name] = `Sample ${col.name} ${idx + 1}`;
        }
      });
      return row;
    });
  };

  const downloadAndDisplayTable = async () => {
    setIsLoadingTable(true);
    try {
      if (window.sharePointAPI?.downloadPpeAdl) {
        const result = await window.sharePointAPI.downloadPpeAdl();
        if (result.success && result.data) {
          setTableData(result.data);

          // Parse the JSON data structure: { Additions: {...}, Deletions: {...} }
          const data = result.data;

          // Extract table data from Additions and Deletions
          // The structure might be: { Additions: { Total: ..., "In the last 15 days": ..., ... }, Deletions: {...} }
          // Or it might have table arrays
          if (data.Additions) {
            // Load saved textResponse and documents if they exist (before filtering)
            if (data.Additions.textResponse) {
              setAdditionsTextResponse(data.Additions.textResponse);
            }
            if (data.Additions.documents && Array.isArray(data.Additions.documents)) {
              setAdditionsDocuments(data.Additions.documents.map((doc: any) => ({
                id: `doc_${doc.name || doc}`,
                type: doc.type || 'linked',
                name: doc.name || doc,
                url: doc.url,
                reference: doc.reference
              })));
            }

            // Convert Additions object to array format if needed
            // Filter out textResponse, documents, and table fields from display
            if (Array.isArray(data.Additions)) {
              // Filter out rows that are metadata fields
              const filteredAdditions = data.Additions.map((row: any) => {
                if (typeof row === 'object' && row !== null) {
                  const { textResponse, documents, table, ...displayRow } = row;
                  return displayRow;
                }
                return row;
              });
              setAdditionsTableData(filteredAdditions);
            } else if (typeof data.Additions === 'object') {
              // Convert object to array of rows, excluding metadata fields
              const additionsRows = Object.entries(data.Additions)
                .filter(([key]) => key !== 'textResponse' && key !== 'documents' && key !== 'table')
                .map(([key, value]) => ({
                  Metric: key,
                  Value: value
                }));
              setAdditionsTableData(additionsRows);
            }
          }

          if (data.Deletions) {
            // Load saved textResponse and documents if they exist (before filtering)
            if (data.Deletions.textResponse) {
              setDeletionsTextResponse(data.Deletions.textResponse);
            }
            if (data.Deletions.documents && Array.isArray(data.Deletions.documents)) {
              setDeletionsDocuments(data.Deletions.documents.map((doc: any) => ({
                id: `doc_${doc.name || doc}`,
                type: doc.type || 'linked',
                name: doc.name || doc,
                url: doc.url,
                reference: doc.reference
              })));
            }

            // Convert Deletions object to array format if needed
            // Filter out textResponse, documents, and table fields from display
            if (Array.isArray(data.Deletions)) {
              // Filter out rows that are metadata fields
              const filteredDeletions = data.Deletions.map((row: any) => {
                if (typeof row === 'object' && row !== null) {
                  const { textResponse, documents, table, ...displayRow } = row;
                  return displayRow;
                }
                return row;
              });
              setDeletionsTableData(filteredDeletions);
            } else if (typeof data.Deletions === 'object') {
              // Convert object to array of rows, excluding metadata fields
              const deletionsRows = Object.entries(data.Deletions)
                .filter(([key]) => key !== 'textResponse' && key !== 'documents' && key !== 'table')
                .map(([key, value]) => ({
                  Metric: key,
                  Value: value
                }));
              setDeletionsTableData(deletionsRows);
            }
          }
        }
      }

      // If no data from SharePoint, use dummy data for display
      if (additionsTableData.length === 0 && additionColumns.length > 0) {
        setAdditionsTableData(generateDummyTableData(additionColumns, 15));
      }
      if (deletionsTableData.length === 0 && deletionColumns.length > 0) {
        setDeletionsTableData(generateDummyTableData(deletionColumns, 10));
      }

      setShowAnalysisSelection(true);
    } catch (error) {
      console.error("Error downloading table data:", error);
      // Fallback to dummy data
      if (additionColumns.length > 0) {
        setAdditionsTableData(generateDummyTableData(additionColumns, 15));
      }
      if (deletionColumns.length > 0) {
        setDeletionsTableData(generateDummyTableData(deletionColumns, 10));
      }
      setShowAnalysisSelection(true);
    } finally {
      setIsLoadingTable(false);
    }
  };

  const additionsColumnsForAnalysis = useMemo(() => {
    return additionColumns
      .filter(col => col.name !== additionsDateCol && col.name !== additionsAmountCol)
      .map(col => col.name);
  }, [additionColumns, additionsDateCol, additionsAmountCol]);

  const handleColumnToggle = (column: string) => {
    setSelectedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(column)) {
        newSet.delete(column);
      } else {
        newSet.add(column);
      }
      return newSet;
    });
  };

  const generateAnalysis = () => {
    if (selectedColumns.size === 0 || additionsTableData.length === 0) return;

    setIsGeneratingAnalysis(true);
    const analyses: Record<string, any> = {};

    selectedColumns.forEach(column => {
      const valueCounts: Record<string, number> = {};
      const valueAmounts: Record<string, number> = {};
      const amountCol = additionsAmountCol;

      additionsTableData.forEach((row: any) => {
        const value = row[column];
        if (value !== null && value !== undefined && value !== '') {
          const key = String(value);
          valueCounts[key] = (valueCounts[key] || 0) + 1;
          if (amountCol && row[amountCol]) {
            const amount = parseFloat(String(row[amountCol]).replace(/,/g, '')) || 0;
            valueAmounts[key] = (valueAmounts[key] || 0) + amount;
          }
        }
      });

      const columnData = additionsTableData.map((row: any) => row[column]).filter((val: any) => val !== null && val !== undefined && val !== '');

      const chartData = Object.entries(valueCounts).map(([name, count]) => ({
        name,
        value: count,
        amount: valueAmounts[name] || 0
      }));

      const tableDataForColumn = Object.entries(valueCounts).map(([name, count]) => ({
        value: name,
        count,
        amount: valueAmounts[name] || 0,
        percentage: columnData.length > 0 ? ((count / columnData.length) * 100).toFixed(2) + '%' : '0%'
      })).sort((a, b) => b.count - a.count);

      analyses[column] = {
        chartData,
        tableData: tableDataForColumn,
        totalCount: columnData.length,
        uniqueValues: Object.keys(valueCounts).length
      };
    });

    setColumnAnalyses(analyses);
    setIsGeneratingAnalysis(false);
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

  // Save tables with textResponse and documents to SharePoint
  const saveTables = useCallback(async () => {
    setSaveStatus('saving');
    setSaveError(null);

    try {
      // Trigger auto-save for both DocumentManagers to ensure latest content is captured
      const additionsAutoSave = (window as any)[`autoSaveTextbox_PPE_ADL_Additions`];
      const deletionsAutoSave = (window as any)[`autoSaveTextbox_PPE_ADL_Deletions`];

      if (additionsAutoSave && typeof additionsAutoSave === 'function') {
        additionsAutoSave();
      }
      if (deletionsAutoSave && typeof deletionsAutoSave === 'function') {
        deletionsAutoSave();
      }

      // Also capture content directly from contentEditable editors
      // The DocumentManager wraps content in a div with data-document-manager and data-item-id
      const additionsEditor = document.querySelector('[data-document-manager][data-item-id="PPE_ADL_Additions"] [contenteditable="true"]') as HTMLElement;
      const deletionsEditor = document.querySelector('[data-document-manager][data-item-id="PPE_ADL_Deletions"] [contenteditable="true"]') as HTMLElement;

      if (additionsEditor && additionsEditor.innerHTML.trim()) {
        const htmlContent = additionsEditor.innerHTML;
        localStorage.setItem('textbox_PPE_ADL_Additions', htmlContent);
        console.log('[PPE ADL] Saved additions textbox content directly from editor, length:', htmlContent.length);
      }

      if (deletionsEditor && deletionsEditor.innerHTML.trim()) {
        const htmlContent = deletionsEditor.innerHTML;
        localStorage.setItem('textbox_PPE_ADL_Deletions', htmlContent);
        console.log('[PPE ADL] Saved deletions textbox content directly from editor, length:', htmlContent.length);
      }

      // Wait a bit to ensure state updates complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Check localStorage FIRST (highest priority) - same as planning phase
      const additionsTextboxContent = localStorage.getItem('textbox_PPE_ADL_Additions');
      const deletionsTextboxContent = localStorage.getItem('textbox_PPE_ADL_Deletions');

      const hasAdditionsTextboxContent = additionsTextboxContent && additionsTextboxContent.trim().length > 0;
      const hasDeletionsTextboxContent = deletionsTextboxContent && deletionsTextboxContent.trim().length > 0;

      const hasAdditionsTextResponse = additionsTextResponse && (
        (additionsTextResponse.html && additionsTextResponse.html.trim().length > 0) ||
        (additionsTextResponse.plainText && additionsTextResponse.plainText.trim().length > 0)
      );

      const hasDeletionsTextResponse = deletionsTextResponse && (
        (deletionsTextResponse.html && deletionsTextResponse.html.trim().length > 0) ||
        (deletionsTextResponse.plainText && deletionsTextResponse.plainText.trim().length > 0)
      );

      // Build textResponse for Additions - localStorage takes priority
      let additionsTextResponseData = null;
      if (hasAdditionsTextboxContent) {
        additionsTextResponseData = {
          html: additionsTextboxContent,
          plainText: additionsTextboxContent.replace(/<[^>]*>/g, '') // Strip HTML tags for plain text
        };
        console.log('[PPE ADL] Using additions textbox content from localStorage, length:', additionsTextboxContent.length);
      } else if (hasAdditionsTextResponse) {
        // Ensure it has both html and plainText
        if (additionsTextResponse.html && !additionsTextResponse.plainText) {
          additionsTextResponseData = {
            html: additionsTextResponse.html,
            plainText: additionsTextResponse.html.replace(/<[^>]*>/g, '')
          };
        } else if (additionsTextResponse.plainText && !additionsTextResponse.html) {
          additionsTextResponseData = {
            html: additionsTextResponse.plainText,
            plainText: additionsTextResponse.plainText
          };
        } else {
          additionsTextResponseData = additionsTextResponse;
        }
        console.log('[PPE ADL] Using additions textResponse from state');
      }

      // Build textResponse for Deletions - localStorage takes priority
      let deletionsTextResponseData = null;
      if (hasDeletionsTextboxContent) {
        deletionsTextResponseData = {
          html: deletionsTextboxContent,
          plainText: deletionsTextboxContent.replace(/<[^>]*>/g, '') // Strip HTML tags for plain text
        };
        console.log('[PPE ADL] Using deletions textbox content from localStorage, length:', deletionsTextboxContent.length);
      } else if (hasDeletionsTextResponse) {
        // Ensure it has both html and plainText
        if (deletionsTextResponse.html && !deletionsTextResponse.plainText) {
          deletionsTextResponseData = {
            html: deletionsTextResponse.html,
            plainText: deletionsTextResponse.html.replace(/<[^>]*>/g, '')
          };
        } else if (deletionsTextResponse.plainText && !deletionsTextResponse.html) {
          deletionsTextResponseData = {
            html: deletionsTextResponse.plainText,
            plainText: deletionsTextResponse.plainText
          };
        } else {
          deletionsTextResponseData = deletionsTextResponse;
        }
        console.log('[PPE ADL] Using deletions textResponse from state');
      }

      // Prepare the data structure to save
      const dataToSave: any = {
        Additions: {
          ...(tableData?.Additions && typeof tableData.Additions === 'object' && !Array.isArray(tableData.Additions)
            ? Object.fromEntries(Object.entries(tableData.Additions).filter(([key]) => key !== 'textResponse' && key !== 'documents'))
            : {}),
          textResponse: additionsTextResponseData,
          documents: additionsDocuments.filter(doc => doc.type !== 'textbox').map(doc => ({
            name: doc.name,
            type: doc.type,
            url: doc.url,
            reference: doc.reference
          }))
        },
        Deletions: {
          ...(tableData?.Deletions && typeof tableData.Deletions === 'object' && !Array.isArray(tableData.Deletions)
            ? Object.fromEntries(Object.entries(tableData.Deletions).filter(([key]) => key !== 'textResponse' && key !== 'documents'))
            : {}),
          textResponse: deletionsTextResponseData,
          documents: deletionsDocuments.filter(doc => doc.type !== 'textbox').map(doc => ({
            name: doc.name,
            type: doc.type,
            url: doc.url,
            reference: doc.reference
          }))
        }
      };

      console.log('[PPE ADL] Saving tables with textResponse:', {
        additionsHasTextResponse: !!additionsTextResponseData,
        additionsHtmlLength: additionsTextResponseData?.html?.length || 0,
        additionsPlainTextLength: additionsTextResponseData?.plainText?.length || 0,
        deletionsHasTextResponse: !!deletionsTextResponseData,
        deletionsHtmlLength: deletionsTextResponseData?.html?.length || 0,
        deletionsPlainTextLength: deletionsTextResponseData?.plainText?.length || 0
      });

      // Save to SharePoint via IPC
      if (window.sharePointAPI?.savePpeAdlTable) {
        const result = await window.sharePointAPI.savePpeAdlTable(dataToSave);
        if (result.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 3000);
        } else {
          throw new Error(result.error || 'Save failed');
        }
      } else {
        throw new Error('SharePoint API not available');
      }

      // Save inference question to Execution_PPE_Responses.json
      await new Promise(resolve => setTimeout(resolve, 500));
      try {
        const inferenceId = "PPE-ADL-INFERENCE";
        const areaPrefix = "PPE";
        const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
        let inferenceDocumentNames: string[] = [];
        try {
          if ((window as any).sharePointAPI?.loadDocumentListBySection) {
            const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
            if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
              inferenceDocumentNames = dbResult.data
                .map((file: any) => file.name || '')
                .filter((name: string) => name && name.trim().length > 0);
            }
          }
        } catch (error) {
          console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
        }

        const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
        const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
        
        let inferenceTextResponseData = null;
        if (hasInferenceTextboxContent) {
          inferenceTextResponseData = {
            html: inferenceTextboxFromLocalStorage,
            plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
          };
        } else if (inferenceQuestion.textResponse) {
          inferenceTextResponseData = inferenceQuestion.textResponse;
        }

        const inferenceFileName = `Execution_${areaPrefix}_Responses.json`;
        let existingData: any = {};
        try {
          const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(inferenceFileName);
          if (downloadResult?.success && downloadResult?.data) {
            existingData = downloadResult.data;
          }
        } catch (error) {
          console.warn(`Failed to download existing ${inferenceFileName}, starting from empty`, error);
        }

        if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
          existingData = {};
        }

        if (!existingData.substantiveProcedures) {
          existingData.substantiveProcedures = {};
        }

        existingData.substantiveProcedures[inferenceId] = {
          selectedOption: "",
          textResponse: inferenceTextResponseData,
          documents: inferenceDocumentNames,
          table: null,
          lastUpdated: new Date().toISOString(),
          companyName: "ABC Private Limited", // You may want to load this dynamically
        };

        const jsonString = JSON.stringify(existingData, null, 2);
        const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

        const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
          fileContent: base64Content,
          fileName: inferenceFileName,
          referenceValue: `Execution ${areaPrefix} Substantive Responses`,
          folderName: "juggernaut",
          fyYear: getFY_YEAR(),
          sectionList: [`Execution_${areaPrefix}_Responses`],
        });

        if (!uploadResult || !uploadResult.success) {
          console.warn("Failed to save inference question:", uploadResult?.error);
        }
      } catch (error) {
        console.warn("Failed to save inference question:", error);
      }
    } catch (error) {
      console.error('Failed to save tables:', error);
      setSaveStatus('error');
      setSaveError(error instanceof Error ? error.message : 'Failed to save tables');
    }
  }, [tableData, additionsTextResponse, additionsDocuments, deletionsTextResponse, deletionsDocuments, inferenceQuestion]);

  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Get text from localStorage if available (DocumentManager auto-saves)
      const itemId = 'PPE-ADL-INFERENCE';
      const localStorageKey = `autoSaveTextbox_${itemId}`;
      const savedText = localStorage.getItem(localStorageKey) || inferenceQuestion.textResponse || '';

      const fileName = 'Execution_PPE_Responses.json';

      // Download existing file
      let existingData: any = {};
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result.data) {
          existingData = result.data;
        }
      } catch (error) {
        console.warn('Failed to download existing file, starting fresh:', error);
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Wait a bit for localStorage sync
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Query db.json for linked/uploaded documents
      const section = `Execution_PPE_PPE-ADL-INFERENCE`;
      let documents: any[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const docResult = await (window as any).sharePointAPI.loadDocumentListBySection(section);
          if (docResult?.success && Array.isArray(docResult.data)) {
            documents = docResult.data;
          }
        }
      } catch (error) {
        console.warn('Failed to load documents from db.json:', error);
      }

      // Update inference question data
      existingData.substantiveProcedures[itemId] = {
        selectedOption: 'textbox',
        textResponse: {
          html: savedText,
          plainText: savedText
        },
        documents: documents,
        lastUpdated: new Date().toISOString(),
        companyName: companyName
      };

      // Upload updated file
      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }

      const result = await (window as any).sharePointAPI.uploadFile({
        fileContent: base64Content,
        fileName: fileName,
        referenceValue: `Execution PPE Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_PPE_Responses`],
      });

      if (!result?.success) {
        throw new Error(result?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Failed to save inference question:', error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Additions & Deletions Analysis - Property, Plant & Equipment</h2>
          <p className="text-gray-400">Analyze additions and deletions to PPE</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button onClick={searchForFiles} disabled={fileSearchStatus === "searching"} className="bg-blue-600 hover:bg-blue-700 text-white">
              {fileSearchStatus === "searching" ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Searching...</>
              ) : (
                "Search for Files"
              )}
            </Button>
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-white">Additions Listing</Label>
                  <Select value={additionsFile} onValueChange={setAdditionsFile}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select additions file" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {foundFiles.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Search for Files - Additions"]) && (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {fileUsageReference.find(e => e["Search for Files - Additions"])?.["Search for Files - Additions"]}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Deletions Listing</Label>
                  <Select value={deletionsFile} onValueChange={setDeletionsFile}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select deletions file" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {foundFiles.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Search for Files - Deletions"]) && (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {fileUsageReference.find(e => e["Search for Files - Deletions"])?.["Search for Files - Deletions"]}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={loadAdditionColumns} disabled={!additionsFile || isLoadingAddCols} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {isLoadingAddCols ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
                  ) : (
                    "Load Additions Columns"
                  )}
                </Button>
                <Button onClick={loadDeletionColumns} disabled={!deletionsFile || isLoadingDelCols} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {isLoadingDelCols ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
                  ) : (
                    "Load Deletions Columns"
                  )}
                </Button>
              </div>
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">No files found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button onClick={uploadFile} disabled={uploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {uploadStatus === "uploading" ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload File</>
                  )}
                </Button>
                {uploadStatus === "completed" && (
                  <span className="text-green-400">File uploaded successfully!</span>
                )}
                {uploadStatus === "error" && (
                  <span className="text-red-400">Upload failed</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 2: Map Columns
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 2: Map Columns"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Additions Columns */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Additions Listing Columns</h3>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Date Column</Label>
                  <Select value={additionsDateCol} onValueChange={setAdditionsDateCol}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select date column" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {additionColumns.map((col) => (
                        <SelectItem key={col.value} value={col.value}>
                          {col.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                    try {
                      const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                      const lastUsed = mapping.additions?.dateColumn;
                      return lastUsed ? (
                        <p className="text-xs text-white/60 mt-1">
                          Last used: {lastUsed}
                        </p>
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Amount Column</Label>
                  <Select value={additionsAmountCol} onValueChange={setAdditionsAmountCol}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select amount column" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {additionColumns.map((col) => (
                        <SelectItem key={col.value} value={col.value}>
                          {col.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                    try {
                      const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                      const lastUsed = mapping.additions?.amountColumn;
                      return lastUsed ? (
                        <p className="text-xs text-white/60 mt-1">
                          Last used: {lastUsed}
                        </p>
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </div>
              </div>
            </div>

            {/* Deletions Columns */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Deletions Listing Columns</h3>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Date Column</Label>
                  <Select value={deletionsDateCol} onValueChange={setDeletionsDateCol}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select date column" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {deletionColumns.map((col) => (
                        <SelectItem key={col.value} value={col.value}>
                          {col.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                    try {
                      const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                      const lastUsed = mapping.deletions?.dateColumn;
                      return lastUsed ? (
                        <p className="text-xs text-white/60 mt-1">
                          Last used: {lastUsed}
                        </p>
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Amount Column</Label>
                  <Select value={deletionsAmountCol} onValueChange={setDeletionsAmountCol}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select amount column" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {deletionColumns.map((col) => (
                        <SelectItem key={col.value} value={col.value}>
                          {col.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                    try {
                      const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                      const lastUsed = mapping.deletions?.amountColumn;
                      return lastUsed ? (
                        <p className="text-xs text-white/60 mt-1">
                          Last used: {lastUsed}
                        </p>
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5 text-green-500" />
              Execute Additions & Deletions Analysis
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Execute Additions & Deletions Analysis"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button onClick={runADL} disabled={!canRun() || processingStatus === "running"} className="bg-green-600 hover:bg-green-700 text-white">
              {processingStatus === "running" ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                "Run Additions & Deletions Analysis"
              )}
            </Button>
            {processingStatus === "completed" && (
              <span className="text-green-400">Successful</span>
            )}
            {processingStatus === "error" && (
              <span className="text-red-400">Failed</span>
            )}
          </div>
          {fileUrl && (
            <div className="mt-4 p-3 bg-green-900/20 border border-green-500/20 rounded-lg">
              <p className="text-green-400 text-sm">Additions & Deletions analysis completed! Results uploaded to SharePoint.</p>
              <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline text-sm">View Results</a>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              Analysis Results
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Analysis Results"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={downloadAndDisplayTable}
            disabled={isLoadingTable}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isLoadingTable ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
            ) : (
              "Analysis Results"
            )}
          </Button>

          {(additionsTableData.length > 0 || deletionsTableData.length > 0) && (
            <div className="space-y-6">
              {/* Additions Table */}
              {additionsTableData.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Additions Listing</h3>
                  <div className="overflow-x-auto max-h-96 border border-white/20 rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-white/5">
                          {Object.keys(additionsTableData[0] || {})
                            .filter(key => key !== 'textResponse' && key !== 'documents' && key !== 'table')
                            .map((key) => (
                              <TableHead key={key} className="border border-white/20 px-4 py-2 text-left text-white font-semibold text-sm">
                                {key}
                              </TableHead>
                            ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {additionsTableData.map((row: any, idx: number) => (
                          <TableRow key={idx} className="border-white/10 hover:bg-white/5">
                            {Object.keys(additionsTableData[0] || {})
                              .filter(key => key !== 'textResponse' && key !== 'documents' && key !== 'table')
                              .map((key) => (
                                <TableCell key={key} className="border border-white/20 px-4 py-2 text-white text-sm">
                                  {row[key] || '-'}
                                </TableCell>
                              ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-4">
                    <DocumentManager
                      sectionKey="payroll"
                      itemId="PPE_ADL_Additions"
                      itemQuestion="Additions Listing Documentation"
                      itemTextResponse={additionsTextResponse}
                      itemDocuments={additionsDocuments}
                      onDocumentsChange={(docs) => setAdditionsDocuments(docs)}
                      onTextResponseChange={(textResp) => setAdditionsTextResponse(textResp)}
                      disabled={false}
                    />
                  </div>
                </div>
              )}

              {/* Deletions Table */}
              {deletionsTableData.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Deletions Listing</h3>
                  <div className="overflow-x-auto max-h-96 border border-white/20 rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-white/5">
                          {Object.keys(deletionsTableData[0] || {})
                            .filter(key => key !== 'textResponse' && key !== 'documents' && key !== 'table')
                            .map((key) => (
                              <TableHead key={key} className="border border-white/20 px-4 py-2 text-left text-white font-semibold text-sm">
                                {key}
                              </TableHead>
                            ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deletionsTableData.map((row: any, idx: number) => (
                          <TableRow key={idx} className="border-white/10 hover:bg-white/5">
                            {Object.keys(deletionsTableData[0] || {})
                              .filter(key => key !== 'textResponse' && key !== 'documents' && key !== 'table')
                              .map((key) => (
                                <TableCell key={key} className="border border-white/20 px-4 py-2 text-white text-sm">
                                  {row[key] || '-'}
                                </TableCell>
                              ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-4">
                    <DocumentManager
                      sectionKey="payroll"
                      itemId="PPE_ADL_Deletions"
                      itemQuestion="Deletions Listing Documentation"
                      itemTextResponse={deletionsTextResponse}
                      itemDocuments={deletionsDocuments}
                      onDocumentsChange={(docs) => setDeletionsDocuments(docs)}
                      onTextResponseChange={(textResp) => setDeletionsTextResponse(textResp)}
                      disabled={false}
                    />
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div className="text-sm text-white/60">
                  Save both tables with documentation to SharePoint
                </div>
                <Button
                  onClick={saveTables}
                  disabled={saveStatus === 'saving'}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {saveStatus === 'saving' ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" /> Save Tables</>
                  )}
                </Button>
              </div>

              {/* Save Status Messages */}
              {saveStatus === 'saved' && (
                <div className="rounded-md bg-green-500/10 border border-green-500/20 p-3 text-sm text-green-300">
                  Tables saved successfully to Execution_PPE_ADL.json
                </div>
              )}
              {saveStatus === 'error' && saveError && (
                <div className="rounded-md bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-300">
                  Error saving tables: {saveError}
                </div>
              )}

              {/* Analysis Results */}
              {Object.keys(columnAnalyses).length > 0 && (
                <div className="space-y-8">
                  <h3 className="text-xl font-semibold text-white">Column Analysis Results</h3>
                  {Object.entries(columnAnalyses).map(([column, analysis]: [string, any]) => (
                    <div key={column} className="space-y-4 p-4 border border-white/20 rounded-lg bg-white/5">
                      <h4 className="text-lg font-semibold text-white">{column} Analysis</h4>

                      {/* Pie Chart */}
                      {analysis.chartData.length > 0 && (
                        <div className="space-y-4">
                          <h5 className="text-md font-medium text-white/80">Distribution Chart</h5>
                          <div className="w-full h-80">
                            <ResponsiveContainer width="100%" height="100%">
                              <RechartsPieChart>
                                <Pie
                                  data={analysis.chartData}
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={100}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {analysis.chartData.map((entry: any, index: number) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                              </RechartsPieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}

                      {/* Summary Table */}
                      <div className="space-y-4">
                        <h5 className="text-md font-medium text-white/80">Summary Table</h5>
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse border border-white/20">
                            <thead>
                              <tr className="bg-black/60">
                                <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Value</th>
                                <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Count</th>
                                <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Percentage</th>
                                {additionsAmountCol && (
                                  <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Total Amount</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {analysis.tableData.map((row: any, idx: number) => (
                                <tr key={idx} className="hover:bg-white/5">
                                  <td className="border border-white/20 px-4 py-2 text-white">{row.value}</td>
                                  <td className="border border-white/20 px-4 py-2 text-white">{row.count}</td>
                                  <td className="border border-white/20 px-4 py-2 text-white">{row.percentage}</td>
                                  {additionsAmountCol && (
                                    <td className="border border-white/20 px-4 py-2 text-white">
                                      {formatNumber(row.amount)}
                                    </td>
                                  )}
                                </tr>
                              ))}
                              <tr className="bg-black/40 font-semibold">
                                <td className="border border-white/20 px-4 py-2 text-white">Total</td>
                                <td className="border border-white/20 px-4 py-2 text-white">{analysis.totalCount}</td>
                                <td className="border border-white/20 px-4 py-2 text-white">100%</td>
                                {additionsAmountCol && (
                                  <td className="border border-white/20 px-4 py-2 text-white">
                                    {formatNumber(analysis.tableData.reduce((sum: number, row: any) => sum + row.amount, 0))}
                                  </td>
                                )}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Document Button */}
                      <div className="flex items-center gap-2 pt-2">
                        <FileText className="h-4 w-4 text-white/60" />
                        <span className="text-sm text-white/80">Document:</span>
                        <Button variant="outline" className="border-white/10 text-white/80 hover:bg-white/10 hover:text-white">
                          Document
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Composition Analysis Section */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <PieChart className="h-5 w-5 text-purple-500" />
            Composition Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Additions Composition Analysis */}
          <div className="space-y-4 p-4 border border-white/20 rounded-lg bg-white/5">
            <h3 className="text-lg font-semibold text-white">Additions Composition Analysis</h3>
            <p className="text-sm text-white/60">Select columns from the Additions file to analyze composition by unique values and amounts.</p>

            <div className="space-y-3">
              <Label className="text-white">Select Columns for Analysis</Label>
              <div className="relative" ref={additionsDropdownRef}>
                <Button
                  variant="outline"
                  className="w-full justify-between border-white/10 bg-black/40 text-white hover:bg-white/10"
                  onClick={() => {
                    setShowAdditionsCompositionDropdown(!showAdditionsCompositionDropdown);
                    if (showAdditionsCompositionDropdown) setAdditionsCompositionColumnsSearch("");
                  }}
                  disabled={additionColumns.length === 0}
                >
                  {additionsCompositionColumns.size > 0
                    ? `${additionsCompositionColumns.size} column(s) selected`
                    : additionColumns.length === 0
                      ? "Load Additions Columns first"
                      : "Select columns"}
                </Button>
                {showAdditionsCompositionDropdown && additionColumns.length > 0 && (
                  <div className="absolute z-10 mt-2 w-full rounded-md border border-white/10 bg-black/90 p-3 max-h-64 overflow-auto">
                    <div className="mb-2 sticky top-0 bg-black/90 z-10">
                      <Input
                        type="text"
                        placeholder="Search columns..."
                        value={additionsCompositionColumnsSearch}
                        onChange={(e) => setAdditionsCompositionColumnsSearch(e.target.value)}
                        className="w-full border-white/10 bg-black/40 text-white placeholder:text-white/40"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="space-y-2">
                      {additionColumns
                        .filter((col) => col.name.toLowerCase().includes(additionsCompositionColumnsSearch.toLowerCase()) || col.value.toLowerCase().includes(additionsCompositionColumnsSearch.toLowerCase()))
                        .map((col) => (
                          <div key={col.value} className="flex items-center space-x-2 px-2 py-1 hover:bg-white/5 rounded">
                            <Checkbox
                              id={`add-comp-${col.value}`}
                              checked={additionsCompositionColumns.has(col.value)}
                              onCheckedChange={() => handleAdditionsCompositionColumnToggle(col.value)}
                            />
                            <Label htmlFor={`add-comp-${col.value}`} className="text-white text-sm cursor-pointer">
                              {col.name}
                            </Label>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              {additionsCompositionColumns.size > 0 && (
                <div className="text-xs text-blue-300">
                  Selected: {Array.from(additionsCompositionColumns).join(', ')}
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-white">Amount Column</Label>
                <Select value={additionsCompositionAmountCol} onValueChange={setAdditionsCompositionAmountCol}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select amount column" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {additionColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Run Additions Composition Analysis Button */}
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <div className="text-sm text-white/60">
                Run composition analysis for additions
              </div>
              <Button
                onClick={runAdditionsCompositionAnalysis}
                disabled={
                  isRunningAdditionsCompositionAnalysis ||
                  additionsCompositionColumns.size === 0 ||
                  !additionsCompositionAmountCol
                }
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isRunningAdditionsCompositionAnalysis ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running Analysis...</>
                ) : (
                  <><PieChart className="h-4 w-4 mr-2" /> Run Additions Composition Analysis</>
                )}
              </Button>
            </div>

            {/* Additions Composition Results */}
            {Object.keys(additionsCompositionResults).length > 0 && (
              <div className="space-y-6 mt-6">
                {Object.entries(additionsCompositionResults).map(([column, analysis]: [string, any]) => {
                  const columnResponse = additionsCompositionResponses[column] || {};
                  const columnTextResponse = columnResponse.textResponse;
                  const columnDocuments = columnResponse.documents || [];

                  return (
                    <div key={column} className="space-y-4 p-4 border border-white/20 rounded-lg bg-black/20">
                      <h4 className="text-md font-semibold text-white">{column} Composition</h4>
                      {analysis.chartData && analysis.chartData.length > 0 && (
                        <div className="w-full h-80">
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPieChart>
                              <Pie
                                data={analysis.chartData}
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {analysis.chartData.map((entry: any, index: number) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip
                                formatter={(value: any, name: string, props: any) => {
                                  if (name === 'value') {
                                    return [`${value} items`, 'Count'];
                                  }
                                  return [value, name];
                                }}
                              />
                              <Legend />
                            </RechartsPieChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                      {/* Document Manager for Additions Composition Analysis */}
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="mb-3">
                          <h5 className="text-sm font-medium text-white">Documentation</h5>
                        </div>
                        <DocumentManager
                          sectionKey="ppe-additions-composition"
                          itemId={column}
                          itemQuestion={`${column} Composition Analysis`}
                          itemTextResponse={columnTextResponse}
                          itemDocuments={columnDocuments}
                          onTextResponseChange={(textResponse) => {
                            setAdditionsCompositionResponses(prev => ({
                              ...prev,
                              [column]: {
                                ...prev[column],
                                textResponse
                              }
                            }));
                          }}
                          onDocumentsChange={(documents) => {
                            setAdditionsCompositionResponses(prev => ({
                              ...prev,
                              [column]: {
                                ...prev[column],
                                documents
                              }
                            }));
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          </div>

          {/* Deletions Composition Analysis */}
          <div className="space-y-4 p-4 border border-white/20 rounded-lg bg-white/5">
            <h3 className="text-lg font-semibold text-white">Deletions Composition Analysis</h3>
            <p className="text-sm text-white/60">Select columns from the Deletions file to analyze composition by unique values and amounts.</p>

            <div className="space-y-3">
              <Label className="text-white">Select Columns for Analysis</Label>
              <div className="relative" ref={deletionsDropdownRef}>
                <Button
                  variant="outline"
                  className="w-full justify-between border-white/10 bg-black/40 text-white hover:bg-white/10"
                  onClick={() => {
                    setShowDeletionsCompositionDropdown(!showDeletionsCompositionDropdown);
                    if (showDeletionsCompositionDropdown) setDeletionsCompositionColumnsSearch("");
                  }}
                  disabled={deletionColumns.length === 0}
                >
                  {deletionsCompositionColumns.size > 0
                    ? `${deletionsCompositionColumns.size} column(s) selected`
                    : deletionColumns.length === 0
                      ? "Load Deletions Columns first"
                      : "Select columns"}
                </Button>
                {showDeletionsCompositionDropdown && deletionColumns.length > 0 && (
                  <div className="absolute z-10 mt-2 w-full rounded-md border border-white/10 bg-black/90 p-3 max-h-64 overflow-auto">
                    <div className="mb-2 sticky top-0 bg-black/90 z-10">
                      <Input
                        type="text"
                        placeholder="Search columns..."
                        value={deletionsCompositionColumnsSearch}
                        onChange={(e) => setDeletionsCompositionColumnsSearch(e.target.value)}
                        className="w-full border-white/10 bg-black/40 text-white placeholder:text-white/40"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="space-y-2">
                      {deletionColumns
                        .filter((col) => col.name.toLowerCase().includes(deletionsCompositionColumnsSearch.toLowerCase()) || col.value.toLowerCase().includes(deletionsCompositionColumnsSearch.toLowerCase()))
                        .map((col) => (
                          <div key={col.value} className="flex items-center space-x-2 px-2 py-1 hover:bg-white/5 rounded">
                            <Checkbox
                              id={`del-comp-${col.value}`}
                              checked={deletionsCompositionColumns.has(col.value)}
                              onCheckedChange={() => handleDeletionsCompositionColumnToggle(col.value)}
                            />
                            <Label htmlFor={`del-comp-${col.value}`} className="text-white text-sm cursor-pointer">
                              {col.name}
                            </Label>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              {deletionsCompositionColumns.size > 0 && (
                <div className="text-xs text-blue-300">
                  Selected: {Array.from(deletionsCompositionColumns).join(', ')}
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-white">Amount Column</Label>
                <Select value={deletionsCompositionAmountCol} onValueChange={setDeletionsCompositionAmountCol}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select amount column" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {deletionColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Run Deletions Composition Analysis Button */}
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <div className="text-sm text-white/60">
                Run composition analysis for deletions
              </div>
              <Button
                onClick={runDeletionsCompositionAnalysis}
                disabled={
                  isRunningDeletionsCompositionAnalysis ||
                  deletionsCompositionColumns.size === 0 ||
                  !deletionsCompositionAmountCol
                }
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isRunningDeletionsCompositionAnalysis ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running Analysis...</>
                ) : (
                  <><PieChart className="h-4 w-4 mr-2" /> Run Deletions Composition Analysis</>
                )}
              </Button>
            </div>

            {/* Deletions Composition Results */}
            {Object.keys(deletionsCompositionResults).length > 0 && (
              <div className="space-y-6 mt-6">
                {Object.entries(deletionsCompositionResults).map(([column, analysis]: [string, any]) => {
                  const columnResponse = deletionsCompositionResponses[column] || {};
                  const columnTextResponse = columnResponse.textResponse;
                  const columnDocuments = columnResponse.documents || [];

                  return (
                    <div key={column} className="space-y-4 p-4 border border-white/20 rounded-lg bg-black/20">
                      <h4 className="text-md font-semibold text-white">{column} Composition</h4>
                      {analysis.chartData && analysis.chartData.length > 0 && (
                        <div className="w-full h-80">
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPieChart>
                              <Pie
                                data={analysis.chartData}
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {analysis.chartData.map((entry: any, index: number) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip
                                formatter={(value: any, name: string, props: any) => {
                                  if (name === 'value') {
                                    return [`${value} items`, 'Count'];
                                  }
                                  return [value, name];
                                }}
                              />
                              <Legend />
                            </RechartsPieChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                      {/* Document Manager for Deletions Composition Analysis */}
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="mb-3">
                          <h5 className="text-sm font-medium text-white">Documentation</h5>
                        </div>
                        <DocumentManager
                          sectionKey="ppe-deletions-composition"
                          itemId={column}
                          itemQuestion={`${column} Composition Analysis`}
                          itemTextResponse={columnTextResponse}
                          itemDocuments={columnDocuments}
                          onTextResponseChange={(textResponse) => {
                            setDeletionsCompositionResponses(prev => ({
                              ...prev,
                              [column]: {
                                ...prev[column],
                                textResponse
                              }
                            }));
                          }}
                          onDocumentsChange={(documents) => {
                            setDeletionsCompositionResponses(prev => ({
                              ...prev,
                              [column]: {
                                ...prev[column],
                                documents
                              }
                            }));
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          </div>

          {/* Run Analysis Button */}
          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <div className="text-sm text-white/60">
              Run composition analysis for selected columns
            </div>
            <Button
              onClick={runCompositionAnalysis}
              disabled={
                isRunningCompositionAnalysis ||
                (additionsCompositionColumns.size === 0 && deletionsCompositionColumns.size === 0) ||
                (!additionsAmountCol && !deletionsAmountCol)
              }
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isRunningCompositionAnalysis ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running Analysis...</>
              ) : (
                <><PieChart className="h-4 w-4 mr-2" /> Run Composition Analysis</>
              )}
            </Button>
          </div>

          {/* Load Analysis Section */}
          <div className="pt-6 border-t border-white/10">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-white/60">
                Load composition analysis results from SharePoint
              </div>
            </div>

            <div className="flex items-center gap-4 mb-4">
              <Button
                onClick={loadCompositionAnalysis}
                disabled={isLoadingCompositionAnalysis}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isLoadingCompositionAnalysis ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
                ) : (
                  <><PieChart className="h-4 w-4 mr-2" /> Load Additions Analysis</>
                )}
              </Button>

              <Button
                onClick={loadDeletionsCompositionAnalysis}
                disabled={isLoadingDeletionsAnalysis}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isLoadingDeletionsAnalysis ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
                ) : (
                  <><PieChart className="h-4 w-4 mr-2" /> Load Deletions Analysis</>
                )}
              </Button>
            </div>

            {/* Display Loaded Results */}
            {(Object.keys(loadedAdditionsCompositionData).length > 0 || Object.keys(loadedDeletionsCompositionData).length > 0) ? (
              <div className="space-y-8 mt-6">
                {/* Additions Results */}
                {Object.keys(loadedAdditionsCompositionData).length > 0 && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-white">Additions Composition Analysis</h3>
                    {Object.entries(loadedAdditionsCompositionData).map(([columnName, columnData]: [string, any]) => {
                      // Handle both array format and object format with data/textResponse/documents
                      let dataArray: any[] = [];
                      if (Array.isArray(columnData)) {
                        dataArray = columnData;
                      } else if (columnData && typeof columnData === 'object' && Array.isArray(columnData.data)) {
                        dataArray = columnData.data;
                      } else {
                        console.log(`Skipping ${columnName}: invalid format`, columnData);
                        return null;
                      }

                      // Use absolute amounts so negative accounting values still produce visible charts
                      const totalAmount = dataArray.reduce(
                        (sum: number, item: any) => sum + Math.abs(item.amount || 0),
                        0
                      );
                      const chartData = dataArray.map((item: any) => ({
                        name: String(item.label || ''),
                        value: Math.abs(Number(item.amount || 0))
                      })).filter(item => item.value !== 0);

                      console.log(`Rendering chart for ${columnName}:`, { chartData, totalAmount });

                      const columnResponse = additionsCompositionResponses[columnName] || {};
                      // Get textResponse and documents from loaded data or state
                      const loadedColumnData = Array.isArray(columnData) ? null : columnData;
                      const columnTextResponse = loadedColumnData?.textResponse || columnResponse.textResponse;
                      const columnDocuments = loadedColumnData?.documents || columnResponse.documents || [];

                      return (
                        <div key={columnName} className="p-4 border border-white/20 rounded-lg bg-white/5">
                          <h4 className="text-lg font-semibold text-white mb-4">{columnName}</h4>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Pie Chart */}
                            {chartData && chartData.length > 0 ? (
                              <div className="w-full" style={{ minHeight: '320px', height: '320px' }}>
                                <ResponsiveContainer width="100%" height={320}>
                                  <RechartsPieChart>
                                    <Pie
                                      data={chartData}
                                      cx="50%"
                                      cy="50%"
                                      outerRadius={100}
                                      fill="#8884d8"
                                      dataKey="value"
                                    >
                                      {chartData.map((entry: any, index: number) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                      ))}
                                    </Pie>
                                    <Tooltip
                                      formatter={(value: any) => [
                                        `${formatNumber(Number(value))}`,
                                        'Amount'
                                      ]}
                                    />
                                    <Legend />
                                  </RechartsPieChart>
                                </ResponsiveContainer>
                              </div>
                            ) : (
                              <div className="w-full h-80 flex items-center justify-center text-white/60 border border-white/10 rounded">
                                No chart data available (chartData length: {chartData?.length || 0})
                              </div>
                            )}

                            {/* Table */}
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse border border-white/20">
                                <thead>
                                  <tr className="bg-black/60">
                                    <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Label</th>
                                    <th className="border border-white/20 px-4 py-2 text-right text-white font-semibold">Amount</th>
                                    <th className="border border-white/20 px-4 py-2 text-right text-white font-semibold">Percentage</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dataArray.map((item: any, idx: number) => {
                                const amount = Math.abs(item.amount || 0);
                                const percentage = totalAmount > 0 ? (amount / totalAmount * 100).toFixed(2) : '0.00';
                                    return (
                                      <tr key={idx} className="hover:bg-white/5">
                                        <td className="border border-white/20 px-4 py-2 text-white">{String(item.label || '')}</td>
                                        <td className="border border-white/20 px-4 py-2 text-white text-right">
                                          {formatNumber(Number(amount))}
                                        </td>
                                        <td className="border border-white/20 px-4 py-2 text-white text-right">{percentage}%</td>
                                      </tr>
                                    );
                                  })}
                                  <tr className="bg-black/40 font-semibold">
                                    <td className="border border-white/20 px-4 py-2 text-white">Total</td>
                                    <td className="border border-white/20 px-4 py-2 text-white text-right">
                                      {formatNumber(totalAmount)}
                                    </td>
                                    <td className="border border-white/20 px-4 py-2 text-white text-right">100%</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                          {/* Document Manager for Additions Composition Analysis */}
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="mb-3">
                              <h5 className="text-sm font-medium text-white">Documentation</h5>
                            </div>
                            <DocumentManager
                              sectionKey="ppe-additions-composition"
                              itemId={columnName}
                              itemQuestion={`${columnName} Composition Analysis`}
                              itemTextResponse={columnTextResponse}
                              itemDocuments={columnDocuments}
                              onTextResponseChange={(textResponse) => {
                                setAdditionsCompositionResponses(prev => ({
                                  ...prev,
                                  [columnName]: {
                                    ...prev[columnName],
                                    textResponse
                                  }
                                }));
                              }}
                              onDocumentsChange={(documents) => {
                                setAdditionsCompositionResponses(prev => ({
                                  ...prev,
                                  [columnName]: {
                                    ...prev[columnName],
                                    documents
                                  }
                                }));
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Deletions Results */}
                {Object.keys(loadedDeletionsCompositionData).length > 0 && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-white">Deletions Composition Analysis</h3>
                    {Object.entries(loadedDeletionsCompositionData).map(([columnName, columnData]: [string, any]) => {
                      // Handle both array format and object format with data/textResponse/documents
                      let dataArray: any[] = [];
                      if (Array.isArray(columnData)) {
                        dataArray = columnData;
                      } else if (columnData && typeof columnData === 'object' && Array.isArray(columnData.data)) {
                        dataArray = columnData.data;
                      } else {
                        return null;
                      }

                      const totalAmount = dataArray.reduce(
                        (sum: number, item: any) => sum + Math.abs(item.amount || 0),
                        0
                      );
                      const chartData = dataArray.map((item: any) => ({
                        name: String(item.label || ''),
                        value: Math.abs(Number(item.amount || 0))
                      })).filter(item => item.value !== 0);

                      const columnResponse = deletionsCompositionResponses[columnName] || {};
                      // Get textResponse and documents from loaded data or state
                      const loadedColumnData = Array.isArray(columnData) ? null : columnData;
                      const columnTextResponse = loadedColumnData?.textResponse || columnResponse.textResponse;
                      const columnDocuments = loadedColumnData?.documents || columnResponse.documents || [];

                      return (
                        <div key={columnName} className="p-4 border border-white/20 rounded-lg bg-white/5">
                          <h4 className="text-lg font-semibold text-white mb-4">{columnName}</h4>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Pie Chart */}
                            {chartData && chartData.length > 0 ? (
                              <div className="w-full" style={{ minHeight: '320px', height: '320px' }}>
                                <ResponsiveContainer width="100%" height={320}>
                                  <RechartsPieChart>
                                    <Pie
                                      data={chartData}
                                      cx="50%"
                                      cy="50%"
                                      outerRadius={100}
                                      fill="#8884d8"
                                      dataKey="value"
                                    >
                                      {chartData.map((entry: any, index: number) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                      ))}
                                    </Pie>
                                    <Tooltip
                                      formatter={(value: any) => [
                                        `${formatNumber(Number(value))}`,
                                        'Amount'
                                      ]}
                                    />
                                    <Legend />
                                  </RechartsPieChart>
                                </ResponsiveContainer>
                              </div>
                            ) : (
                              <div className="w-full h-80 flex items-center justify-center text-white/60 border border-white/10 rounded">
                                No chart data available
                              </div>
                            )}

                            {/* Table */}
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse border border-white/20">
                                <thead>
                                  <tr className="bg-black/60">
                                    <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Label</th>
                                    <th className="border border-white/20 px-4 py-2 text-right text-white font-semibold">Amount</th>
                                    <th className="border border-white/20 px-4 py-2 text-right text-white font-semibold">Percentage</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dataArray.map((item: any, idx: number) => {
                                  const amount = Math.abs(item.amount || 0);
                                  const percentage = totalAmount > 0 ? (amount / totalAmount * 100).toFixed(2) : '0.00';
                                    return (
                                      <tr key={idx} className="hover:bg-white/5">
                                        <td className="border border-white/20 px-4 py-2 text-white">{String(item.label || '')}</td>
                                        <td className="border border-white/20 px-4 py-2 text-white text-right">
                                          {formatNumber(Number(amount))}
                                        </td>
                                        <td className="border border-white/20 px-4 py-2 text-white text-right">{percentage}%</td>
                                      </tr>
                                    );
                                  })}
                                  <tr className="bg-black/40 font-semibold">
                                    <td className="border border-white/20 px-4 py-2 text-white">Total</td>
                                    <td className="border border-white/20 px-4 py-2 text-white text-right">
                                      {formatNumber(totalAmount)}
                                    </td>
                                    <td className="border border-white/20 px-4 py-2 text-white text-right">100%</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                          {/* Document Manager for Deletions Composition Analysis */}
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="mb-3">
                              <h5 className="text-sm font-medium text-white">Documentation</h5>
                            </div>
                            <DocumentManager
                              sectionKey="ppe-deletions-composition"
                              itemId={columnName}
                              itemQuestion={`${columnName} Composition Analysis`}
                              itemTextResponse={columnTextResponse}
                              itemDocuments={columnDocuments}
                              onTextResponseChange={(textResponse) => {
                                setDeletionsCompositionResponses(prev => ({
                                  ...prev,
                                  [columnName]: {
                                    ...prev[columnName],
                                    textResponse
                                  }
                                }));
                              }}
                              onDocumentsChange={(documents) => {
                                setDeletionsCompositionResponses(prev => ({
                                  ...prev,
                                  [columnName]: {
                                    ...prev[columnName],
                                    documents
                                  }
                                }));
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Save All Button */}
                {(Object.keys(loadedAdditionsCompositionData).length > 0 || Object.keys(loadedDeletionsCompositionData).length > 0) && (
                  <div className="pt-6 border-t border-white/10 mt-6">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-white/60">
                        Save all composition analysis data to SharePoint
                      </div>
                      <div className="flex items-center gap-3">
                        {saveAllStatus === 'saved' && (
                          <span className="text-sm text-green-400">Saved successfully!</span>
                        )}
                        {saveAllStatus === 'error' && (
                          <span className="text-sm text-red-400">
                            {saveError || "Error saving"}
                          </span>
                        )}
                        <Button
                          onClick={saveAllCompositionAnalysis}
                          disabled={isSavingAllComposition}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {isSavingAllComposition ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                          ) : (
                            <><Save className="h-4 w-4 mr-2" /> Save All</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-white/60 mt-4">
                No data loaded. Click "Load Additions Analysis" or "Load Deletions Analysis" to download results from SharePoint.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="ppe-additions-deletions"
            itemId="PPE-ADL-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Step 2: Map Columns")]: "Step 2: Map Columns",
              [getSectionQuestionId("Execute Additions & Deletions Analysis")]: "Execute Additions & Deletions Analysis",
              [getSectionQuestionId("Analysis Results")]: "Analysis Results",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
          workspace={workspaceName}
          subPhase={procedureName}
        />
      )}
    </div>
  );
}

